import NumberFlow, { NumberFlowElement } from '@number-flow/react';
import classNames from 'classnames';
import { isPast } from 'date-fns';
import {
  AnimatePresence,
  motion,
  useInView,
  useReducedMotion,
  useScroll,
  useTransform,
} from 'motion/react';
import type {
  GetStaticPropsContext,
  GetStaticPropsResult,
  InferGetStaticPropsType,
} from 'next';
import NextHead from 'next/head';
import NextImage from 'next/legacy/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import {
  CSSProperties,
  forwardRef,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ClickOutside } from '../../../components/ClickOutside';
import { HideHubspot } from '../../../components/HideHubspot';
import Head from '../../../components/Layout/Head';
import { useTheme } from '../../../components/Layout/Theme/useTheme';
import SocialMetadata from '../../../components/SocialMetadata';
import { imageLoader } from '../../../helpers/images';
import { toCanonicalUrl } from '../../../helpers/links';
import { imageOgStaticLoader } from '../../../helpers/og-image';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import styles from '../../../styles/10-decades-of-alex-katz.module.css';
import { Franchises, HeroImage } from '../../../types/Franchises';
import {
  getStoryblokPage,
  StoryResponse,
  useStoryblok,
} from '../../../utils/storyblok';
import { pageTranslations } from '../../../utils/translations';

function createZIndexMap(length: number) {
  return Array.from({ length }, (_, i) => {
    const mid = (length - 1) / 2;
    const distanceFromMiddle = Math.abs(i - mid);
    return Math.round((length - distanceFromMiddle) * 10);
  });
}

// ----- MENU ----- //

const Menu = ({ active }: { active: string | null }) => {
  const shouldReduceMotion = useReducedMotion();

  const [isOpen, setIsOpen] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const [menuWidth, setMenuWidth] = useState(384);
  const contentRef = useRef<HTMLDivElement>(null);

  const calculateDimensions = () => {
    const viewportWidth = document.documentElement.clientWidth;
    const maxWidth = 384;
    const padding = 48; // match right-6 = 24 * 2
    const width = Math.min(maxWidth, viewportWidth - padding);
    setMenuWidth(width);

    if (contentRef.current) {
      const element = contentRef.current;
      element.style.width = `${width}px`;
      const height = element.offsetHeight;
      setContentHeight(height);
    }
  };

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(calculateDimensions);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('resize', calculateDimensions);
      return () => window.removeEventListener('resize', calculateDimensions);
    }
    return undefined;
  }, [isOpen]);

  useEffect(() => {
    const sections = document.querySelectorAll('section');

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      sections.forEach((section) => {
        section.setAttribute('inert', '');
      });
    } else {
      document.body.style.overflow = '';
      sections.forEach((section) => {
        section.removeAttribute('inert');
      });
    }

    return () => {
      document.body.style.overflow = '';
      sections.forEach((section) => {
        section.removeAttribute('inert');
      });
    };
  }, [isOpen]);

  return (
    <ClickOutside onClickOutside={() => setIsOpen(false)}>
      <motion.div
        className={classNames(styles.menuWrapper, 'will-change-auto')}
        layout
        style={{
          width: isOpen ? menuWidth : 44,
          // Stay at 44 until the content is measured; `layout` can't scale from a 0px box
          height: isOpen && contentHeight ? contentHeight : 44,
          // Inline so `layout` can scale-correct the corners while it animates
          borderRadius: 6,
        }}
        transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30,
          duration: shouldReduceMotion ? 0 : 0.6,
        }}
      >
        <AnimatePresence>
          {isOpen ? (
            <motion.div layout>
              <motion.div
                ref={contentRef}
                animate={{
                  opacity: 1,
                  scale: 1,
                }}
                className={classNames(styles.menuContent, 'will-change-auto')}
                exit={{
                  opacity: 0,
                  scale: 0.95,
                }}
                initial={{
                  opacity: 0,
                  scale: 0.95,
                }}
                transition={{
                  duration: shouldReduceMotion ? 0 : 0.2,
                  delay: shouldReduceMotion ? 0 : 0.1,
                }}
              >
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col gap-3 will-change-transform"
                  initial={{ opacity: 0, y: 10 }}
                  transition={{
                    duration: shouldReduceMotion ? 0 : 0.3,
                    delay: shouldReduceMotion ? 0 : 0.2,
                  }}
                >
                  <motion.p
                    animate={{ opacity: 1, y: 0 }}
                    className={classNames(
                      styles.menuContent__title,
                      'will-change-transform',
                    )}
                    initial={{ opacity: 0, y: 10 }}
                    transition={{
                      duration: shouldReduceMotion ? 0 : 0.3,
                      delay: shouldReduceMotion ? 0 : 0.25,
                    }}
                  >
                    10 Decades of Alex Katz
                  </motion.p>
                  <motion.p
                    animate={{ opacity: 1, y: 0 }}
                    className={classNames(
                      styles.menuContent__text,
                      'will-change-transform',
                    )}
                    initial={{ opacity: 0, y: 10 }}
                    transition={{
                      duration: shouldReduceMotion ? 0 : 0.3,
                      delay: shouldReduceMotion ? 0 : 0.3,
                    }}
                  >
                    Alex Katz has spent nearly a century painting – beginning
                    amid Abstract Expressionism and later developing the bold,
                    flat style that anticipated Pop Art. Since 1951, his work
                    has been featured in more than 200 solo exhibitions and 500
                    group shows. This timeline charts some of the milestones,
                    transformations and the immense impact of his remarkable
                    journey as an artist.
                  </motion.p>

                  <motion.div
                    animate={{ opacity: 1, y: 0 }}
                    className="will-change-transform"
                    initial={{ opacity: 0, y: 10 }}
                    transition={{
                      duration: shouldReduceMotion ? 0 : 0.3,
                      delay: shouldReduceMotion ? 0 : 0.35,
                    }}
                  >
                    <nav>
                      <ul className={styles.menuContent__timeline}>
                        <li
                          className={styles.menuContent__timelineItem}
                          style={{ '--order': 1 } as CSSProperties}
                        >
                          <Link
                            {...(active === '1920' && {
                              'aria-current': 'page',
                            })}
                            className={styles.menuContent__timelineItemLink}
                            href="#1920"
                          >
                            1920s
                          </Link>
                        </li>
                        <li
                          className={styles.menuContent__timelineItem}
                          style={{ '--order': 2 } as CSSProperties}
                        >
                          <span
                            className={styles.menuContent__timelineItemLink}
                          >
                            1930s
                          </span>
                        </li>
                        <li
                          className={styles.menuContent__timelineItem}
                          style={{ '--order': 3 } as CSSProperties}
                        >
                          <Link
                            {...(active === '1940' && {
                              'aria-current': 'page',
                            })}
                            className={styles.menuContent__timelineItemLink}
                            href="#1940"
                          >
                            1940s
                          </Link>
                        </li>
                        <li
                          className={styles.menuContent__timelineItem}
                          style={{ '--order': 4 } as CSSProperties}
                        >
                          <Link
                            {...(active === '1950' && {
                              'aria-current': 'page',
                            })}
                            className={styles.menuContent__timelineItemLink}
                            href="#1950"
                          >
                            1950s
                          </Link>
                        </li>
                        <li
                          className={styles.menuContent__timelineItem}
                          style={{ '--order': 8 } as CSSProperties}
                        >
                          <Link
                            {...(active === '1960' && {
                              'aria-current': 'page',
                            })}
                            className={styles.menuContent__timelineItemLink}
                            href="#1960"
                          >
                            1960s
                          </Link>
                        </li>
                        <li
                          className={styles.menuContent__timelineItem}
                          style={{ '--order': 7 } as CSSProperties}
                        >
                          <Link
                            {...(active === '1970' && {
                              'aria-current': 'page',
                            })}
                            className={styles.menuContent__timelineItemLink}
                            href="#1970"
                          >
                            1970s
                          </Link>
                        </li>
                        <li
                          className={styles.menuContent__timelineItem}
                          style={{ '--order': 6 } as CSSProperties}
                        >
                          <Link
                            {...(active === '1980' && {
                              'aria-current': 'page',
                            })}
                            className={styles.menuContent__timelineItemLink}
                            href="#1980"
                          >
                            1980s
                          </Link>
                        </li>
                        <li
                          className={styles.menuContent__timelineItem}
                          style={{ '--order': 5 } as CSSProperties}
                        >
                          <Link
                            {...(active === '1990' && {
                              'aria-current': 'page',
                            })}
                            className={styles.menuContent__timelineItemLink}
                            href="#1990"
                          >
                            1990s
                          </Link>
                        </li>
                        <li
                          className={styles.menuContent__timelineItem}
                          style={{ '--order': 9 } as CSSProperties}
                        >
                          <Link
                            {...(active === '2000' && {
                              'aria-current': 'page',
                            })}
                            className={styles.menuContent__timelineItemLink}
                            href="#2000"
                          >
                            2000s
                          </Link>
                        </li>
                        <li
                          className={styles.menuContent__timelineItem}
                          style={{ '--order': 10 } as CSSProperties}
                        >
                          <Link
                            {...(active === '2010' && {
                              'aria-current': 'page',
                            })}
                            className={styles.menuContent__timelineItemLink}
                            href="#2010"
                          >
                            2010s
                          </Link>
                        </li>
                        <li
                          className={styles.menuContent__timelineItem}
                          style={{ '--order': 11 } as CSSProperties}
                        >
                          <Link
                            {...(active === '2020' && {
                              'aria-current': 'page',
                            })}
                            className={styles.menuContent__timelineItemLink}
                            href="#2020"
                          >
                            2020s
                          </Link>
                        </li>
                        <li
                          className={styles.menuContent__timelineItem}
                          style={{ '--order': 12 } as CSSProperties}
                        >
                          <Link
                            {...(active === 'credits' && {
                              'aria-current': 'page',
                            })}
                            className={styles.menuContent__timelineItemLink}
                            href="#credits"
                          >
                            Credits
                          </Link>
                        </li>
                      </ul>
                    </nav>
                  </motion.div>
                </motion.div>
                <motion.p
                  animate={{ opacity: 1, y: 0 }}
                  className={classNames(
                    styles.menuContent_footer,
                    'will-change-transform',
                  )}
                  initial={{ opacity: 0, y: 10 }}
                  transition={{
                    duration: shouldReduceMotion ? 0 : 0.3,
                    delay: shouldReduceMotion ? 0 : 0.4,
                  }}
                >
                  Avant Arte x Guggenheim New York
                  <br />
                  October 2025
                </motion.p>
              </motion.div>
            </motion.div>
          ) : null}
        </AnimatePresence>

        <motion.button
          className={styles.menuButton}
          layout
          onClick={() => setIsOpen(!isOpen)}
          type="button"
        >
          {isOpen ? (
            <>
              <span className="sr-only">Close menu</span>
              <svg
                className="size-5"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </>
          ) : (
            <>
              <span className="sr-only">Open menu</span>
              <svg
                className="size-5"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 5H20M4 12H20M4 19H20"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </>
          )}
        </motion.button>
      </motion.div>
    </ClickOutside>
  );
};

// ----- Date ----- //

const AnimatedDate = forwardRef<
  HTMLParagraphElement,
  {
    date: number;
    className?: string;
  }
>((props, ref) => {
  const elementRef = useRef<NumberFlowElement>(null);

  const isInView = useInView(elementRef, {
    once: true,
    margin: '0px 0px -10% 0px',
  });

  const targetValue = isInView ? props.date : Math.floor(props.date / 10) * 10;

  return (
    <div ref={ref} className={props.className}>
      <NumberFlow
        ref={elementRef}
        format={{
          style: 'decimal',
          useGrouping: false,
        }}
        transformTiming={{
          duration: 1500,
          easing: 'ease-out',
        }}
        value={targetValue}
      />
    </div>
  );
});

// ----- Image ----- //

const Image = ({
  alt,
  priority = false,
  quality,
  src,
  width,
}: {
  src: string;
  alt: string;
  priority: boolean;
  width: number;
  quality?: number;
}) => {
  return (
    <NextImage
      alt={alt}
      className="h-full w-full object-cover"
      layout="fill"
      loader={imageLoader}
      objectFit="cover"
      placeholder="empty"
      priority={priority}
      quality={quality}
      sizes={`${width}px`}
      src={src}
      title={alt}
    />
  );
};

// ----- Video ----- //

const Video = ({
  alt,
  className,
  cover,
  priority = false,
  src,
}: {
  alt: string;
  src: string;
  cover: string;
  className: string;
  priority?: boolean;
}) => {
  const [showVideo, setShowVideo] = useState(false);

  return (
    <div
      className={classNames(
        styles.sectionContent_video,
        className,
        'group relative cursor-pointer overflow-hidden outline-none',
      )}
      onClick={() => setShowVideo(true)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          setShowVideo(true);
        }
      }}
      role="button"
      tabIndex={0}
    >
      {showVideo ? (
        <iframe
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="h-full w-full"
          height="315"
          referrerPolicy="strict-origin-when-cross-origin"
          src={src}
          title={alt}
          width="560"
        />
      ) : (
        <>
          <Image alt={alt} priority={priority} src={cover} width={720} />

          <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/20 motion-safe:transition">
            <span className="flex size-16 items-center justify-center rounded-full bg-black/30 text-white ring-0 ring-white/0 backdrop-blur-md group-hover:scale-110 group-focus-visible:scale-110 group-focus-visible:ring-4 group-focus-visible:ring-white/30 motion-safe:transition">
              <span className="sr-only">Play video</span>
              <svg
                className="size-5"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 5C4.9999 4.64809 5.09265 4.30238 5.26888 3.99779C5.44512 3.69319 5.69861 3.44049 6.00375 3.26519C6.30889 3.0899 6.65488 2.99822 7.00679 2.99942C7.3587 3.00061 7.70406 3.09464 8.008 3.272L20.005 10.27C20.3078 10.4457 20.5591 10.6977 20.7339 11.001C20.9088 11.3043 21.0009 11.6481 21.0012 11.9981C21.0015 12.3482 20.91 12.6922 20.7357 12.9957C20.5614 13.2993 20.3105 13.5518 20.008 13.728L8.008 20.728C7.70406 20.9054 7.3587 20.9994 7.00679 21.0006C6.65488 21.0018 6.30889 20.9101 6.00375 20.7348C5.69861 20.5595 5.44512 20.3068 5.26888 20.0022C5.09265 19.6976 4.9999 19.3519 5 19V5Z"
                  fill="currentColor"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </div>
        </>
      )}
    </div>
  );
};

// ----- Hero ----- //

const SectionHero = forwardRef<HTMLElement>((_props, ref) => {
  const isMobile = useMediaQuery('sm');
  const shouldReduceMotion = useReducedMotion();

  // Wheel
  const radius = isMobile ? 240 : 420;
  const items = useMemo(
    () => [
      {
        id: 'wheel-item-1',
        image: 'https://images.avantarte.org/media/33NbpUfTgYsPkdm05jrvFHktyxl',
        alt: 'Alex Katz, “Art School”, 1951-1952',
      },
      {
        id: 'wheel-item-2',
        image: 'https://images.avantarte.org/media/33NbxPhrQmOsMPlcBliheUbJVYP',
        alt: 'Alex Katz, “Ada”, 1957',
      },
      {
        id: 'wheel-item-3',
        image: 'https://images.avantarte.org/media/33NbzTCxSzGPk11BW8TbkEH8XOQ',
        alt: 'Alex Katz, “Green Scarf”, 1975',
      },
      {
        id: 'wheel-item-4',
        image: 'https://images.avantarte.org/media/33Nc6tqvWnmKIYuvVU8hLaCI6PJ',
        alt: 'Alex Katz, “Paul Taylor Dance Company”, 1963-1964',
      },
      {
        id: 'wheel-item-5',
        image: 'https://images.avantarte.org/media/33Nc8NQinL18m1caCIWNhTHB1JQ',
        alt: 'Alex Katz, “Day Lily”, 1969',
      },
      {
        id: 'wheel-item-6',
        image: 'https://images.avantarte.org/media/33NcIUOa7K2WB4SiyTaymIoZ3cM',
        alt: 'Black and white photograph of Alex Katz at his studio, 1964',
      },
      {
        id: 'wheel-item-7',
        image: 'https://images.avantarte.org/media/33NcBMfGXyUistAi9BeoqkbnxyT',
        alt: 'Alex Katz, “Blue Umbrella 2”, 2020',
      },
      {
        id: 'wheel-item-8',
        image: 'https://images.avantarte.org/media/33NcCx7QKbSbnOx987PSbRDUnR1',
        alt: 'Alex Katz, “Red Coat”, 1983',
      },
      {
        id: 'wheel-item-9',
        image: 'https://images.avantarte.org/media/33NcELVwzhFgwzSRMcvuO3MhvNf',
        alt: 'Alex Katz, “Reflection”, 2018',
      },
      {
        id: 'wheel-item-10',
        image: 'https://images.avantarte.org/media/33NcFhhsyb8Lp2MlClMGQvXt5lG',
        alt: 'Alex Katz, “Black Hat (Alex)”, 2010',
      },
      {
        id: 'wheel-item-11',
        image: 'https://images.avantarte.org/media/33NcHI1ISBbGPijBfzjJQHyxKti',
        alt: 'Alex Katz, “Iris”, 2019',
      },
      {
        id: 'wheel-item-12',
        image: 'https://images.avantarte.org/media/33NcA1Dw8eDczSLkjhuCV5bw3c7',
        alt: 'Black and white photograph of Alex Katz, smiling, wearing a dark suit and white dress shirt, and standing in front of a painting, 2010',
      },
    ],
    [],
  );

  // Animation
  const imagesRef = useRef<HTMLDivElement>(null);
  const wheelRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: imagesRef,
    offset: ['start start', 'end end'],
  });
  const wheelScale = useTransform(
    scrollYProgress,
    [0, 1],
    shouldReduceMotion ? ['100%', '120%'] : ['100%', '200%'],
  );
  const wheelOpacity = useTransform(scrollYProgress, [0.5, 0.75], [1, 0]);
  const wheelRotate = useTransform(
    scrollYProgress,
    [0, 1],
    shouldReduceMotion ? ['0deg', '10deg'] : ['0deg', '80deg'],
  );
  const wheelStyle = {
    opacity: wheelOpacity,
    scale: wheelScale,
    rotate: wheelRotate,
  };

  const h1Y = useTransform(scrollYProgress, [0.35, 0.65], [0, -200]);
  const h1Opacity = useTransform(scrollYProgress, [0.35, 0.65], [1, 0]);
  const h1Style = { y: h1Y, opacity: h1Opacity };

  const h2Y = useTransform(scrollYProgress, [0.35, 0.65], [200, 0]);
  const h2Opacity = useTransform(scrollYProgress, [0.35, 0.65], [0, 1]);
  const h2Style = { y: h2Y, opacity: h2Opacity };

  const scrollButtonOpacity = useTransform(scrollYProgress, [0.75, 1], [1, 0]);
  const scrollButtonStyle = {
    opacity: scrollButtonOpacity,
  };

  const imagePositions = useMemo(
    () =>
      items.map((_, index) => {
        const angle = (index * 360) / items.length;
        const radian = (angle - 90) * (Math.PI / 180);
        const x = Math.round(radius * Math.cos(radian) * 1e10) / 1e10;
        const y = Math.round(radius * Math.sin(radian) * 1e10) / 1e10;

        return {
          angle,
          x,
          y,
          style: {
            left: `calc(50% + ${x}px)`,
            top: `calc(50% + ${y}px)`,
            transform: `translate(-50%, -50%) rotate(${angle}deg)`,
          } as CSSProperties,
        };
      }),
    [items, radius],
  );

  return (
    <section
      ref={ref}
      className={classNames(styles.sectionWrapper, 'items-center !py-0')}
      id="hero"
    >
      <div ref={imagesRef} className="h-[300lvh] w-full">
        <div className="sticky top-0 grid h-svh w-full grid-cols-1 grid-rows-1 px-6">
          <motion.div
            ref={wheelRef}
            className="relative col-start-1 row-start-1 aspect-[1/1] max-h-svh max-w-full self-center justify-self-center will-change-transform"
            style={wheelStyle}
          >
            <div className={styles.wheelContainer}>
              {items.map((item, index) => (
                <div
                  key={item.id || index}
                  className="absolute aspect-[2/3] w-20 md:w-36"
                  style={imagePositions[index].style}
                >
                  <Image
                    alt={item.alt}
                    priority
                    quality={50}
                    src={item.image}
                    width={120}
                  />
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            className="relative col-start-1 row-start-1 flex w-full max-w-sm flex-col items-center justify-center self-center justify-self-center will-change-transform"
            style={h1Style}
          >
            <h1 className={styles.sectionContent_headline}>
              Ten Decades
              <br />
              of Alex&nbsp;Katz
            </h1>
          </motion.div>

          <motion.div
            className="relative col-start-1 row-start-1 flex w-full max-w-sm flex-col items-center justify-center self-center justify-self-center will-change-transform"
            style={h2Style}
          >
            <p className="text-balance text-center">
              Alex Katz has spent nearly a century painting – beginning amid
              Abstract Expressionism and later developing the bold, flat style
              that anticipated Pop Art. Since 1951, his work has been featured
              in more than 200 solo exhibitions and 500 group shows. This
              timeline charts some of the milestones, transformations and the
              immense impact of his remarkable journey as an artist.
            </p>
          </motion.div>

          <motion.div className={styles.scrollButton} style={scrollButtonStyle}>
            <span className="sr-only">Scroll</span>
            <svg
              className="size-6"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19 9C19 5.13401 15.866 2 12 2C8.13401 2 5 5.13401 5 9V15C5 18.866 8.13401 22 12 22C15.866 22 19 18.866 19 15V9Z"
                fill="none"
                stroke="currentColor"
              />
              <path
                className={classNames(styles.scrollButton_mouseBounce)}
                d="M12 6V10"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </motion.div>
        </div>
      </div>
    </section>
  );
});

// ----- 1920 ----- //

const Section1920 = forwardRef<HTMLElement, { isActive: boolean }>(
  (props, ref) => {
    const isMobile = useMediaQuery('sm');
    const shouldReduceMotion = useReducedMotion();

    const imagesRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({
      target: imagesRef,
      offset: ['start end', 'end start'],
    });

    const image1Y = useTransform(
      scrollYProgress,
      [0, 1],
      shouldReduceMotion ? [-50, 50] : [-200, 200],
    );
    const image1Rotate = useTransform(
      scrollYProgress,
      [0, 1],
      shouldReduceMotion ? [-3, -3] : [-6, -2],
    );

    const image2Y = useTransform(
      scrollYProgress,
      [0, 1],
      shouldReduceMotion ? [25, -25] : [150, -150],
    );
    const image2Rotate = useTransform(
      scrollYProgress,
      [0, 1],
      shouldReduceMotion ? [4, 4] : [7, 1],
    );
    const image2Margin = useTransform(
      scrollYProgress,
      [0, 1],
      shouldReduceMotion ? [0, 0] : [48, -48],
    );

    const image1Style = isMobile
      ? { y: 0, rotate: image1Rotate }
      : { y: image1Y, rotate: image1Rotate };

    const image2Style = isMobile
      ? {
          y: 0,
          rotate: image2Rotate,
          marginTop: image2Margin,
          marginLeft: 0,
        }
      : {
          y: image2Y,
          rotate: image2Rotate,
          marginTop: 0,
          marginLeft: image2Margin,
        };

    return (
      <section
        ref={ref}
        className={classNames(
          styles.sectionWrapper,
          'items-center gap-12 md:gap-72',
        )}
        id="1920"
      >
        <div
          className={classNames(
            styles.sectionWrapper_container,
            'grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-12',
          )}
        >
          <AnimatedDate
            className={classNames(
              styles.sectionContent_date,
              'max-md:text-center md:col-start-1 md:row-start-1 md:justify-self-end',
            )}
            date={1927}
          />

          <p className="flex flex-col max-md:text-center">
            <span className={styles.sectionContent_title}>Born</span>
            <span className={styles.sectionContent_title}>in</span>
            <span className={styles.sectionContent_title}>Brooklyn,</span>
            <span className={styles.sectionContent_title}>New</span>
            <span className={styles.sectionContent_title}>York</span>
          </p>

          <p
            className={classNames(
              styles.sectionContent_text,
              'w-full max-w-md text-center md:col-start-1 md:row-start-1 md:self-end md:justify-self-end md:text-right',
            )}
          >
            Alex Katz is born to Russian Jewish parents who move to the
            residential neighbourhood of St. Albans, Queens, at the outset of
            the Great Depression in 1928. His mother, fluent in six languages,
            encourages him to recite poetry from a young age. He covers the
            family stairwell with crayon drawings and, in second grade, wins a
            citywide drawing competition.
          </p>
        </div>

        <motion.div
          ref={imagesRef}
          animate={{ opacity: 1, visibility: 'visible' }}
          className="flex w-full items-center justify-center px-6 max-md:flex-col"
          initial={{ opacity: 0, visibility: 'hidden' }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className={classNames(
              styles.sectionContent_image,
              'aspect-[3/4] max-w-xs md:max-w-xl',
            )}
            style={image1Style}
          >
            <Image
              alt="Black and white photograph of Alex Katz's father, his brother and himself, 1933"
              priority={props.isActive}
              src="https://images.avantarte.org/media/33CAujNmf6R9WQZ4pSB9wXkYuqP"
              width={480}
            />
          </motion.div>
          <motion.div
            className={classNames(
              styles.sectionContent_image,
              'aspect-[3/4] max-w-xs md:max-w-xl',
            )}
            style={image2Style}
          >
            <Image
              alt="Black and white photograph of Flatbush Avenue, Brooklyn, New York, 1927"
              priority={props.isActive}
              src="https://images.avantarte.org/media/33CAugd9dXkH2oed2eLIXM33TYw"
              width={480}
            />
          </motion.div>
        </motion.div>
      </section>
    );
  },
);

// ----- 1940 ----- //

const Section1940 = forwardRef<HTMLElement, { isActive: boolean }>(
  (props, ref) => {
    const isMobile = useMediaQuery('sm');
    const shouldReduceMotion = useReducedMotion();

    const { scrollYProgress } = useScroll({
      target: ref as RefObject<HTMLElement>,
      offset: ['start center', 'end center'],
    });

    const drawing1Y = useTransform(
      scrollYProgress,
      [0, 1],
      shouldReduceMotion ? ['-10%', '10%'] : [isMobile ? '25%' : '25%', '-25%'],
    );
    const drawing1Style = { x: '50%', y: drawing1Y };

    const drawing2Y = useTransform(
      scrollYProgress,
      [0, 1],
      shouldReduceMotion
        ? ['150%', '100%']
        : [isMobile ? '100%' : '250%', '100%'],
    );
    const drawing2Style = { x: '-70%', y: drawing2Y };

    const imagesRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress: imagesScrollYProgress } = useScroll({
      target: imagesRef,
      offset: ['start end', 'end start'],
    });

    const image1X = useTransform(
      imagesScrollYProgress,
      [0, 1],
      shouldReduceMotion ? ['-10%', '0%'] : ['-50%', '-30%'],
    );
    const image1Style = isMobile ? { x: 0 } : { x: image1X };

    const image2X = useTransform(
      imagesScrollYProgress,
      [0, 1],
      shouldReduceMotion ? ['40%', '30%'] : ['50%', '20%'],
    );
    const image2Style = isMobile ? { x: 0 } : { x: image2X };

    const image3X = useTransform(
      imagesScrollYProgress,
      [0, 1],
      shouldReduceMotion ? ['-30%', '-20%'] : ['-75%', '-50%'],
    );
    const image3Style = isMobile ? { x: 0 } : { x: image3X };

    return (
      <section
        ref={ref}
        className={classNames(
          styles.sectionWrapper,
          'items-center gap-12 sm:gap-24 lg:gap-48',
        )}
        id="1940"
      >
        <motion.div
          className={classNames(
            styles.sectionContent_image,
            'pointer-events-none !absolute aspect-[4/3] mix-blend-multiply lg:max-w-7xl',
          )}
          style={drawing1Style}
        >
          <Image
            alt="Alex Katz, “Man with Newspaper on the Subway”, c. 1940s"
            priority={props.isActive}
            src="https://images.avantarte.org/media/33T0Z584d00b2WTbvcfKKv700fH"
            width={720}
          />
        </motion.div>

        <motion.div
          className={classNames(
            styles.sectionContent_image,
            'pointer-events-none !absolute aspect-[4/3] mix-blend-multiply max-md:hidden lg:max-w-5xl',
          )}
          style={drawing2Style}
        >
          <Image
            alt="Alex Katz, “Two Men”, c. 1940s"
            priority={props.isActive}
            src="https://images.avantarte.org/media/33T1vUO01CbhdkJbPfUnbu9JlBo"
            width={720}
          />
        </motion.div>

        <div
          className={classNames(
            styles.sectionWrapper_container,
            'flex flex-col items-center gap-6 md:gap-12 lg:gap-0',
          )}
        >
          <div
            className={classNames(
              styles.sectionContent_date,
              'flex w-full px-6 max-md:flex-col max-md:items-center md:absolute md:top-0 md:justify-between',
            )}
          >
            <AnimatedDate date={1946} />
            <AnimatedDate date={1949} />
          </div>

          <p className="flex flex-col items-start max-md:-translate-x-[25%] md:items-center md:text-center">
            <span className={classNames(styles.sectionContent_title)}>
              Studies
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'translate-x-[114%] md:translate-x-[28.5%]',
              )}
            >
              at
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'translate-x-[133%] md:translate-x-[80.125%]',
              )}
            >
              the
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'translate-x-[35%] md:translate-x-[41%]',
              )}
            >
              Cooper
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'translate-x-[17.5%] md:translate-x-[66.875%]',
              )}
            >
              Union,
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'translate-x-[91%] md:translate-x-[75%]',
              )}
            >
              New
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'translate-x-[79.75%] md:translate-x-[65.5%]',
              )}
            >
              York
            </span>
          </p>

          <p
            className={classNames(
              styles.sectionContent_text,
              'w-full max-w-sm max-lg:text-center lg:absolute lg:bottom-0 lg:self-start',
            )}
          >
            In the aftermath of WWII, Katz attends The Cooper Union in Lower
            Manhattan, where he discovers his talent for painting. In 1949, he
            receives a scholarship to the Skowhegan School of Painting &
            Sculpture in Maine. His tutors encourage him to paint from life,
            giving him, as he puts it, “a reason to devote my life to painting”.
          </p>
        </div>

        <motion.div
          ref={imagesRef}
          animate={{ opacity: 1, visibility: 'visible' }}
          className="relative flex w-full flex-col items-center space-y-6 px-6 md:space-y-24"
          initial={{ opacity: 0, visibility: 'hidden' }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className={classNames(
              styles.sectionContent_image,
              'aspect-[3/2] bg-white ring ring-8 ring-white sm:max-w-lg lg:max-w-3xl',
            )}
            style={image1Style}
          >
            <Image
              alt="Alex Katz at José de Creeft's sculpture class, Skowhegan, Main, 1946"
              priority={props.isActive}
              src="https://images.avantarte.org/media/33T0BKyPaxOysK2RNpilyvqvyPw"
              width={480}
            />
          </motion.div>
          <motion.div
            className={classNames(
              styles.sectionContent_image,
              'aspect-[2/1] ring ring-8 ring-white sm:max-w-2xl',
            )}
            style={image2Style}
          >
            <Image
              alt="Alex Katz, “Art School”, 1952"
              priority={props.isActive}
              src="https://images.avantarte.org/media/33CHxVwQIez4LzMPRWx11vStMUz"
              width={480}
            />
          </motion.div>
          <motion.div
            className={classNames(
              styles.sectionContent_image,
              'aspect-[3/2] ring ring-8 ring-white sm:max-w-md',
            )}
            style={image3Style}
          >
            <Image
              alt="Alex Katz, “Three Figures On a Subway”, 1948"
              priority={props.isActive}
              src="https://images.avantarte.org/media/33CErJLluCWrIxxaBusg4ASCt3Z"
              width={480}
            />
          </motion.div>
        </motion.div>
      </section>
    );
  },
);

// ----- 1950 ----- //

const Section1950 = forwardRef<HTMLElement, { isActive: boolean }>(
  (props, ref) => {
    const isMobile = useMediaQuery('sm');
    const shouldReduceMotion = useReducedMotion();

    const { scrollYProgress } = useScroll({
      target: ref as RefObject<HTMLElement>,
      offset: ['start center', 'end center'],
    });

    const drawing1X = useTransform(
      scrollYProgress,
      [0, 1],
      shouldReduceMotion
        ? ['-50%', '-50%']
        : [isMobile ? '-20%' : '-50%', '-10%'],
    );
    const drawing1Style = { x: drawing1X, y: '20%' };

    const imagesRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress: imagesScrollYProgress } = useScroll({
      target: imagesRef,
      offset: ['start end', 'end start'],
    });

    const image1X = useTransform(
      imagesScrollYProgress,
      [0, 1],
      shouldReduceMotion
        ? ['-25%', '-25%']
        : [isMobile ? '-25%' : '-75%', '0%'],
    );
    const image1Rotate = useTransform(
      imagesScrollYProgress,
      [0, 1],
      shouldReduceMotion
        ? ['-5deg', '-5deg']
        : [isMobile ? '-7deg' : '-15deg', '2deg'],
    );
    const image1Style = { x: image1X, rotate: image1Rotate };

    const image2X = useTransform(
      imagesScrollYProgress,
      [0, 1],
      shouldReduceMotion ? ['50%', '50%'] : [isMobile ? '50%' : '75%', '0%'],
    );
    const image2Rotate = useTransform(
      imagesScrollYProgress,
      [0, 1],
      shouldReduceMotion
        ? ['7deg', '7deg']
        : [isMobile ? '4deg' : '15deg', '5deg'],
    );
    const image2Style = { x: image2X, rotate: image2Rotate };

    const image3Y = useTransform(
      imagesScrollYProgress,
      [0, 1],
      shouldReduceMotion ? ['20%', '20%'] : ['50%', '-20%'],
    );
    const image3Rotate = useTransform(
      imagesScrollYProgress,
      [0, 1],
      shouldReduceMotion ? ['2deg', '2deg'] : ['-10deg', '5deg'],
    );
    const image3Style = { y: image3Y, rotate: image3Rotate };

    return (
      <section
        ref={ref}
        className={classNames(
          styles.sectionWrapper,
          'items-center max-md:gap-12',
        )}
        id="1950"
      >
        <div
          className={classNames(
            styles.sectionWrapper_container,
            'flex h-0 justify-end gap-x-12',
          )}
        >
          <div className="h-[552px] w-full shrink-0 self-end justify-self-end md:h-[1280px] md:w-[calc(50%_-_24px)]">
            <div
              className={classNames(
                styles.sectionContent_image,
                '!sticky top-6 aspect-[3/2] max-w-full ring ring-8 ring-white md:top-0 md:translate-y-full',
              )}
            >
              <Image
                alt="Alex Katz painting Rudy and Yvonne 1, 1978"
                priority={props.isActive}
                src="https://images.avantarte.org/media/33CFv08k6rd3InsL3GktLB4YMRI"
                width={480}
              />
            </div>
          </div>
        </div>

        <div
          className={classNames(
            styles.sectionWrapper_container,
            'relative z-20 flex flex-col items-center gap-12 md:gap-48',
          )}
        >
          <AnimatedDate
            className={classNames(
              styles.sectionContent_date,
              'max-md:absolute max-md:self-end',
              'md:w-[calc(50%_-_24px)] md:self-start md:text-right',
            )}
            date={1959}
          />

          <p className="flex w-full max-md:flex-col max-md:items-center md:grid md:grid-cols-2 md:gap-x-12">
            <span
              className={classNames(
                styles.sectionContent_title,
                'text-left max-md:self-start md:text-right',
              )}
            >
              Makes
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'text-left max-md:self-start md:row-start-2 md:text-right',
              )}
            >
              his
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'text-left max-md:self-start md:row-start-3 md:text-right',
              )}
            >
              first
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'text-left max-md:self-start md:row-start-4 md:text-right',
              )}
            >
              painted
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'text-left max-md:h-0 max-md:self-start md:row-start-5 md:text-right',
              )}
            >
              cutout
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'max-md:self-end max-md:text-right md:row-start-5',
              )}
            >
              &
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'max-md:self-end max-md:text-right md:col-start-2 md:row-start-6',
              )}
            >
              double
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'max-md:translate-x-[3.25%] max-md:self-end max-md:text-right md:col-start-2 md:row-start-7',
              )}
            >
              portrait,
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'max-md:self-end max-md:text-right md:col-start-2 md:row-start-8',
              )}
            >
              Ada Ada
            </span>
          </p>
        </div>

        <div
          className={classNames(
            styles.sectionWrapper_container,
            'grid gap-x-12 max-md:gap-12 md:-mt-[8%] md:grid-cols-2',
          )}
        >
          <div className="relative flex w-full justify-end">
            <motion.div
              className={classNames(
                styles.sectionContent_image,
                'pointer-events-none !absolute z-0 aspect-[1/2] max-w-full self-end mix-blend-multiply',
              )}
              style={drawing1Style}
            >
              <Image
                alt="Details of the Alex Katz's “Double Portrait”, 1959"
                priority={props.isActive}
                src="https://images.avantarte.org/media/33Sq9Vs2ahx9XEBCmoEl921CmY1"
                width={720}
              />
            </motion.div>

            <div
              className={classNames(
                styles.sectionContent_image,
                'aspect-[1/1] ring ring-8 ring-white md:max-w-lg',
              )}
            >
              <Image
                alt="Alex Katz, “Ada Ada”, 1959"
                priority={props.isActive}
                src="https://images.avantarte.org/media/33SaViiMOwoJ9gv8kFKRSEdO7Mz"
                width={480}
              />
            </div>
          </div>

          <div className="relative z-20 flex w-full max-md:justify-center md:items-end">
            <p
              className={classNames(
                styles.sectionContent_text,
                'w-full max-w-sm max-md:text-center',
              )}
            >
              In 1957, Katz meets biologist Ada Del Moro and marries her just
              three months later. In 1959, he creates his first cutout and
              double portrait, <i>Ada Ada</i>, the first of more than 250
              portraits of his wife and muse over his career. Its bold, graphic
              style anticipates Pop Art, establishing Katz as a key precursor to
              the movement.
            </p>
          </div>
        </div>

        <motion.div
          ref={imagesRef}
          animate={{ opacity: 1, visibility: 'visible' }}
          className="mt-48 flex w-full flex-col items-center -space-y-6 md:mt-96 md:-space-y-24"
          initial={{ opacity: 0, visibility: 'hidden' }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className={classNames(
              styles.sectionContent_image,
              'aspect-[3/2] max-w-xs ring ring-8 ring-white md:max-w-md',
            )}
            style={image1Style}
          >
            <Image
              alt="Black and white photograph of Alex Katz and his wife Ada"
              priority={props.isActive}
              src="https://images.avantarte.org/media/33CHQANqqu3DvnkaEtcO00De2NA"
              width={480}
            />
          </motion.div>
          <motion.div
            className={classNames(
              styles.sectionContent_image,
              'aspect-[1/1] max-w-xs ring ring-8 ring-white md:max-w-md',
            )}
            style={image2Style}
          >
            <Image
              alt="Black and white photograph of Alex Katz and his wife Ada, 1985"
              priority={props.isActive}
              src="https://images.avantarte.org/media/33CHQ4POFQK21myteTJArufAECx"
              width={480}
            />
          </motion.div>
          <motion.div
            className={classNames(
              styles.sectionContent_image,
              'aspect-[3/4] max-w-xs ring ring-8 ring-white md:max-w-sm',
            )}
            style={image3Style}
          >
            <Image
              alt="Black and white photograph of Alex Katz and his wife Ada"
              priority={props.isActive}
              src="https://images.avantarte.org/media/33CGxAA1e9DRnPFjBvkri5hx5HD"
              width={480}
            />
          </motion.div>
        </motion.div>
      </section>
    );
  },
);

// ----- 1960 ----- //

const Section1960 = forwardRef<HTMLElement, { isActive: boolean }>(
  (props, ref) => {
    const isMobile = useMediaQuery('sm');
    const shouldReduceMotion = useReducedMotion();

    const image1Ref = useRef(null);
    const image2Ref = useRef(null);
    const image3Ref = useRef(null);
    const image4Ref = useRef(null);
    const image5Ref = useRef(null);
    const image6Ref = useRef(null);
    const image7Ref = useRef(null);
    const image8Ref = useRef(null);
    const image9Ref = useRef(null);

    const isImage1InView = useInView(image1Ref, {
      once: true,
      margin: '0% 0% -20% 0%',
    });
    const isImage2InView = useInView(image2Ref, {
      once: true,
      margin: '0% 0% -20% 0%',
    });
    const isImage3InView = useInView(image3Ref, {
      once: true,
      margin: '0% 0% -20% 0%',
    });
    const isImage4InView = useInView(image4Ref, {
      once: true,
      margin: '0% 0% -20% 0%',
    });
    const isImage5InView = useInView(image5Ref, {
      once: true,
      margin: '0% 0% -20% 0%',
    });
    const isImage6InView = useInView(image6Ref, {
      once: true,
      margin: '0% 0% -20% 0%',
    });
    const isImage7InView = useInView(image7Ref, {
      once: true,
      margin: '0% 0% -20% 0%',
    });
    const isImage8InView = useInView(image8Ref, {
      once: true,
      margin: '0% 0% -20% 0%',
    });
    const isImage9InView = useInView(image9Ref, {
      once: true,
      margin: '0% 0% -20% 0%',
    });

    // Style constants for each image
    const image1Style = {
      maxWidth: isImage1InView || shouldReduceMotion || isMobile ? 448 : 256,
      transition: shouldReduceMotion || isMobile ? 'none' : 'max-width 300ms',
    };

    const image2Style = {
      maxWidth: isImage2InView || shouldReduceMotion || isMobile ? 448 : 256,
      transition: shouldReduceMotion || isMobile ? 'none' : 'max-width 300ms',
    };

    const image3Style = {
      maxWidth: isImage3InView || shouldReduceMotion || isMobile ? 576 : 256,
      transition: shouldReduceMotion || isMobile ? 'none' : 'max-width 300ms',
    };

    const image4Style = {
      maxWidth: isImage4InView || shouldReduceMotion || isMobile ? 384 : 256,
      transition: shouldReduceMotion || isMobile ? 'none' : 'max-width 300ms',
    };

    const image5Style = {
      maxWidth: isImage5InView || shouldReduceMotion || isMobile ? 384 : 256,
      transition: shouldReduceMotion || isMobile ? 'none' : 'max-width 300ms',
    };

    const image6Style = {
      maxWidth: isImage6InView || shouldReduceMotion || isMobile ? 576 : 256,
      transition: shouldReduceMotion || isMobile ? 'none' : 'max-width 300ms',
    };

    const image7Style = {
      maxWidth: isImage7InView || shouldReduceMotion || isMobile ? 576 : 256,
      transition: shouldReduceMotion || isMobile ? 'none' : 'max-width 300ms',
    };

    const image8Style = {
      maxWidth: isImage8InView || shouldReduceMotion || isMobile ? 512 : 256,
      transition: shouldReduceMotion || isMobile ? 'none' : 'max-width 300ms',
    };

    const image9Style = {
      maxWidth: isImage9InView || shouldReduceMotion || isMobile ? 384 : 256,
      transition: shouldReduceMotion || isMobile ? 'none' : 'max-width 300ms',
    };

    return (
      <section
        ref={ref}
        className={classNames(
          styles.sectionWrapper,
          'items-center gap-36 md:gap-72',
        )}
        id="1960"
      >
        <div
          className={classNames(
            styles.sectionWrapper_container,
            'flex flex-col gap-12',
          )}
        >
          <AnimatedDate
            className={classNames(styles.sectionContent_date, 'text-center')}
            date={1965}
          />

          <p className="flex flex-col items-center text-center">
            <span
              className={classNames(
                styles.sectionContent_title,
                '-translate-x-[0.25%] md:-translate-x-[29%]',
              )}
            >
              Completes
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                '-translate-x-[12%]',
              )}
            >
              his
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                '-translate-x-[17.375%]',
              )}
            >
              very first
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                '-translate-x-[2%] md:-translate-x-[1.875%]',
              )}
            >
              silkscreen,
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                '-translate-x-[2.5%] md:-translate-x-[2.125%]',
              )}
            >
              Luna Park 1
            </span>
          </p>
        </div>

        <div className="grid w-full grid-cols-1 px-6 max-md:gap-y-6 md:grid-cols-2">
          <p
            className={classNames(
              styles.sectionContent_text,
              'w-full max-w-md max-md:justify-self-center max-md:text-center md:col-start-2 md:row-start-1 md:pl-6',
            )}
          >
            Although Katz experiments with drypoint, linoleum and woodblock
            printing before this, <i>Luna Park 1</i> marks the beginning of his
            mature printmaking practice – with its flat colouring and hard-edged
            composition going on to characterise his printmaking career for
            decades to come.
          </p>

          <motion.div
            ref={image1Ref}
            className={classNames(
              styles.sectionContent_image,
              'aspect-[600/795] justify-self-center md:col-start-1 md:row-start-1 md:justify-self-end',
            )}
            style={image1Style}
          >
            <Image
              alt="Alex Katz, “Luna Park 1”, 1965"
              priority={props.isActive}
              src="https://images.avantarte.org/media/33EShnzvzBCwSoUtFVncUWRDO7L"
              width={480}
            />
          </motion.div>

          <motion.div
            ref={image2Ref}
            className={classNames(
              styles.sectionContent_image,
              'aspect-[780/590] max-md:justify-self-center md:col-start-2',
            )}
            style={image2Style}
          >
            <Image
              alt="Alex Katz, “Beach House”"
              priority={props.isActive}
              src="https://images.avantarte.org/media/33ESKHI4728QPifVfib0DCTReTl"
              width={480}
            />
          </motion.div>

          <motion.div
            ref={image3Ref}
            className={classNames(
              styles.sectionContent_image,
              'aspect-[780/640] justify-self-center md:justify-self-end',
            )}
            style={image3Style}
          >
            <Image
              alt="Alex Katz, “Blueberry Field”, 1968"
              priority={props.isActive}
              src="https://images.avantarte.org/media/33VQFU1GCexPTks2jUk5Sk6fLmE"
              width={480}
            />
          </motion.div>

          <motion.div
            ref={image4Ref}
            className={classNames(
              styles.sectionContent_image,
              'aspect-[1080/860] max-md:justify-self-center md:col-start-2 md:row-start-4',
            )}
            style={image4Style}
          >
            <Image
              alt="Alex Katz, “Orange Interior”, 1968"
              priority={props.isActive}
              src="https://images.avantarte.org/media/33ESpe0CMYsYlzmdRy3QaNhKuQX"
              width={480}
            />
          </motion.div>

          <motion.div
            ref={image5Ref}
            className={classNames(
              styles.sectionContent_image,
              'aspect-[800/630] justify-self-center md:row-start-5 md:justify-self-end',
            )}
            style={image5Style}
          >
            <Image
              alt="Alex Katz, Grey Interior”, 1968"
              priority={props.isActive}
              src="https://images.avantarte.org/media/33ESpjqs1bwMImXDlVaPAXakKlf"
              width={480}
            />
          </motion.div>

          <motion.div
            ref={image6Ref}
            className={classNames(
              styles.sectionContent_image,
              'aspect-[1200/1070] max-md:justify-self-center md:col-start-2 md:row-start-6',
            )}
            style={image6Style}
          >
            <Image
              alt="Alex Katz, “Poppy”, 1967"
              priority={props.isActive}
              src="https://images.avantarte.org/media/33ET3PrdPAuDSp9PI4V143RV77j"
              width={480}
            />
          </motion.div>

          <motion.div
            ref={image7Ref}
            className={classNames(
              styles.sectionContent_image,
              'aspect-[780/575] justify-self-center md:row-start-7 md:justify-self-end',
            )}
            style={image7Style}
          >
            <Image
              alt="Alex Katz, “Day Lily 2”, 1969"
              priority={props.isActive}
              src="https://images.avantarte.org/media/33ET3PFuq2nyH3Rk7ZYeOLgokiR"
              width={480}
            />
          </motion.div>

          <motion.div
            ref={image8Ref}
            className={classNames(
              styles.sectionContent_image,
              'aspect-[780/580] max-md:justify-self-center md:col-start-2 md:row-start-8',
            )}
            style={image8Style}
          >
            <Image
              alt="Alex Katz, “Day Lily”, 1969"
              priority={props.isActive}
              src="https://images.avantarte.org/media/33ET3O4zhXC6YsLFnHCWiNHObGS"
              width={480}
            />
          </motion.div>

          <motion.div
            ref={image9Ref}
            className={classNames(
              styles.sectionContent_image,
              'aspect-[600/810] justify-self-center md:row-start-9 md:justify-self-end',
            )}
            style={image9Style}
          >
            <Image
              alt="Alex Katz, “White Petunia”, 1969"
              priority={props.isActive}
              src="https://images.avantarte.org/media/33ETIK5lmeBkws1duiiyKVV5wst"
              width={480}
            />
          </motion.div>
        </div>
      </section>
    );
  },
);

// ----- 1970 ----- //

const Section1970 = forwardRef<HTMLElement, { isActive: boolean }>(
  (props, ref) => {
    const isMobile = useMediaQuery('sm');
    const shouldReduceMotion = useReducedMotion();

    const { scrollYProgress } = useScroll({
      target: ref as RefObject<HTMLElement>,
      offset: ['start center', 'end center'],
    });

    const image1X = useTransform(
      scrollYProgress,
      [0, 1],
      shouldReduceMotion
        ? ['-20%', '-20%']
        : [isMobile ? '-20%' : '-30%', '-15%'],
    );
    const image1Rotate = useTransform(
      scrollYProgress,
      [0, 1],
      shouldReduceMotion ? ['-4deg', '-4deg'] : ['-6deg', '-3deg'],
    );
    const image1Style = { x: image1X, rotate: image1Rotate };

    const image2Y = useTransform(
      scrollYProgress,
      [0, 1],
      shouldReduceMotion ? ['8%', '8%'] : [isMobile ? '12%' : '12%', '8%'],
    );
    const image2Rotate = useTransform(
      scrollYProgress,
      [0, 1],
      shouldReduceMotion ? ['6deg', '6deg'] : ['0deg', '6deg'],
    );
    const image2Style = {
      x: '15%',
      y: image2Y,
      rotate: image2Rotate,
    };

    const image3X = useTransform(
      scrollYProgress,
      [0, 1],
      shouldReduceMotion ? ['35%', '35%'] : [isMobile ? '40%' : '50%', '25%'],
    );
    const image3Rotate = useTransform(
      scrollYProgress,
      [0, 1],
      shouldReduceMotion ? ['4deg', '4deg'] : ['6deg', '2deg'],
    );
    const image3Style = {
      x: image3X,
      y: '-10%',
      rotate: image3Rotate,
    };

    return (
      <section
        ref={ref}
        className={classNames(
          styles.sectionWrapper,
          'items-center gap-24 md:gap-48',
        )}
        id="1970"
      >
        <div
          className={classNames(
            styles.sectionWrapper_container,
            'grid gap-6 md:grid-cols-2 md:gap-12',
          )}
        >
          <AnimatedDate
            className={classNames(
              styles.sectionContent_date,
              'max-md:text-center md:col-start-2 md:row-start-1',
            )}
            date={1977}
          />

          <p className="flex flex-col items-center text-center md:col-start-1 md:row-start-1">
            <span
              className={classNames(
                styles.sectionContent_title,
                '[text-orientation:upright] [writing-mode:vertical-rl]',
              )}
            >
              Takes
            </span>
            <span className={classNames(styles.sectionContent_title)}>
              over
            </span>
            <span className={classNames(styles.sectionContent_title)}>
              New York’s
            </span>
            <span className={classNames(styles.sectionContent_title)}>
              Times Square
            </span>
          </p>

          <p
            className={classNames(
              styles.sectionContent_text,
              'w-full max-w-md max-md:mx-auto max-md:text-center md:col-start-2 md:row-start-1 md:self-end',
            )}
          >
            Katz transforms Times Square with a 247-foot-long, 20-foot-high
            billboard consisting of a frieze composed of 23 portrait heads of
            women, based on studies of nine distinct sitters painted from life.
            Three months of preliminary work go into the project, and the
            physical painting is created by sign painters, primarily painter
            Jerry Johnson, who, under Katz’s supervision, paints wet paint into
            wet paint, just as Katz does in his studio.
          </p>
        </div>

        <div className="flex w-full flex-col items-center gap-24 px-6 md:gap-48">
          <div className="flex w-full items-center justify-center -space-x-16">
            <motion.div
              className={classNames(
                styles.sectionContent_image,
                'aspect-[2/3] max-w-sm ring ring-8 ring-white',
              )}
              style={image1Style}
            >
              <Image
                alt="Alex Katz, “Nine Women”, 1977 in Times Square"
                priority={props.isActive}
                src="https://images.avantarte.org/media/33T9oJ0KSVcdzEmb105TgletBCZ"
                width={480}
              />
            </motion.div>
            <motion.div
              className={classNames(
                styles.sectionContent_image,
                'z-10 aspect-[2/3] max-w-sm ring ring-8 ring-white',
              )}
              style={image2Style}
            >
              <Image
                alt="Alex Katz, “Nine Women”, 1977 in Times Square"
                priority={props.isActive}
                src="https://images.avantarte.org/media/33T9oK9qsI0zwYp05wmnVoMy7Kc"
                width={480}
              />
            </motion.div>
            <motion.div
              className={classNames(
                styles.sectionContent_image,
                'aspect-[2/3] max-w-sm ring ring-8 ring-white',
              )}
              style={image3Style}
            >
              <Image
                alt="Alex Katz, “Nine Women”, 1977 in Times Square"
                priority={props.isActive}
                src="https://images.avantarte.org/media/33T9oSlbZmTEsgeknyW8eMlX6qR"
                width={480}
              />
            </motion.div>
          </div>

          <Video
            alt="Excerpt from the film “Alex Katz: What About Style?”"
            className="aspect-[1/1] max-w-7xl ring ring-8 ring-white md:aspect-[16/9]"
            cover="https://images.avantarte.org/media/33T5SMbgcS2vi4SmY7JOS8oGhJI"
            priority={props.isActive}
            src="https://www.youtube.com/embed/kB6HAKqmY_c?si=Mnu29hnP7_7cf6_L&autoplay=1&controls=0"
          />
        </div>
      </section>
    );
  },
);

// ----- 1980 ----- //

const Section1980 = forwardRef<HTMLElement, { isActive: boolean }>(
  (props, ref) => {
    const isMobile = useMediaQuery('sm');
    const shouldReduceMotion = useReducedMotion();

    const { scrollYProgress } = useScroll({
      target: ref as RefObject<HTMLElement>,
      offset: ['start center', 'end center'],
    });

    const image1Y = useTransform(
      scrollYProgress,
      [0, 1],
      shouldReduceMotion
        ? ['0%', '0%']
        : [isMobile ? '0%' : '30%', isMobile ? '0%' : '-30%'],
    );
    const image1Style = { y: image1Y };

    const image2Y = useTransform(
      scrollYProgress,
      [0, 1],
      shouldReduceMotion ? ['0%', '0%'] : ['30%', '0%'],
    );
    const image2Style = { y: image2Y };

    return (
      <section
        ref={ref}
        className={classNames(styles.sectionWrapper, 'items-center')}
        id="1980"
      >
        <motion.div
          className={classNames(
            styles.sectionContent_image,
            'pointer-events-none !absolute bottom-0 aspect-[1/1] max-w-full',
          )}
          style={image2Style}
        >
          <Image
            alt="Details of Alex Katz's “Red Coat”, 1983"
            priority={props.isActive}
            src="https://images.avantarte.org/media/33TIIILcMGeFrg6m11ClgtRdQtv"
            width={1024}
          />
        </motion.div>

        <div
          className={classNames(
            styles.sectionWrapper_container,
            'grid gap-x-6 gap-y-12 pb-48 md:grid-cols-2 md:gap-x-12 md:gap-y-48 md:pb-72 lg:md:pb-[50%]',
          )}
        >
          <AnimatedDate
            className={classNames(
              styles.sectionContent_date,
              'col-span-full flex justify-center',
            )}
            date={1986}
          />

          <motion.div
            className={classNames(
              styles.sectionContent_image,
              'row-start-3 aspect-[1/2] max-w-xs justify-self-center ring ring-8 ring-white md:col-start-1 md:row-start-2 md:max-w-2xl',
            )}
            style={image1Style}
          >
            <Image
              alt="Poster for the exhibition “Alex Katz” at the Whitney Museum of American Art, New York, 1986"
              priority={props.isActive}
              src="https://images.avantarte.org/media/33THwapAaFYyOyCpE3YBHEOk5fJ"
              width={720}
            />
          </motion.div>

          <div className="w-full justify-self-center md:col-start-2">
            <p className="flex flex-col items-center md:mt-24 md:-translate-x-[10%]">
              <span className={classNames(styles.sectionContent_title)}>
                Survey
              </span>
              <span
                className={classNames(
                  styles.sectionContent_title,
                  'h-0 -translate-x-[75%]',
                )}
              >
                at
              </span>
              <span
                className={classNames(
                  styles.sectionContent_title,
                  'translate-x-[125%]',
                )}
              >
                the
              </span>
              <span
                className={classNames(
                  styles.sectionContent_title,
                  'translate-x-[31.75%]',
                )}
              >
                Whitney
              </span>
              <span
                className={classNames(
                  styles.sectionContent_title,
                  'h-0 -translate-x-[42.25%]',
                )}
              >
                Museum
              </span>
              <span
                className={classNames(
                  styles.sectionContent_title,
                  'translate-x-[129%]',
                )}
              >
                of
              </span>
              <span
                className={classNames(
                  styles.sectionContent_title,
                  'translate-x-[22.625%]',
                )}
              >
                American
              </span>
              <span
                className={classNames(
                  styles.sectionContent_title,
                  'translate-x-[44%]',
                )}
              >
                Art
              </span>
            </p>
          </div>

          <p
            className={classNames(
              styles.sectionContent_text,
              'w-full max-w-md bg-[var(--background)] ring ring-[24px] ring-[var(--background)] max-lg:col-span-full max-lg:justify-self-center max-lg:text-center lg:col-start-2',
            )}
          >
            Katz presents a focused survey at one of the world’s most
            prestigious museums. During the process, he realises he wants to
            push his work in new directions, retrospectively stating: “I wanted
            to move to a place in art that was unstable and terrifying”. This
            renewed sense of urgency inspires him to begin his seminal series of
            night paintings.
          </p>
        </div>
      </section>
    );
  },
);

// ----- 1990 ----- //

const Section1990 = forwardRef<HTMLElement, { isActive: boolean }>(
  (props, ref) => {
    const imagesRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({
      target: imagesRef,
      offset: ['start start', 'end end'],
    });

    const imagesX = useTransform(scrollYProgress, [0, 1], ['0%', '-100%']);
    const imagesStyle = { x: imagesX };

    return (
      <section
        ref={ref}
        className={classNames(styles.sectionWrapper, 'items-center gap-0')}
        id="1990"
      >
        <div
          className={classNames(
            styles.sectionWrapper_container,
            'flex flex-col items-center gap-6 md:gap-12',
          )}
        >
          <AnimatedDate
            className={classNames(styles.sectionContent_date)}
            date={1990}
          />

          <p className="flex w-full max-w-md flex-col items-center text-center max-md:-translate-x-[7%] md:max-w-xl lg:max-w-2xl">
            <span
              className={classNames(styles.sectionContent_title, 'inline-flex')}
            >
              Creates
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'inline-flex h-0 -translate-x-[36%]',
              )}
            >
              the
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'ml-[70%] inline-flex self-start',
              )}
            >
              Alex
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'inline-flex h-0 -translate-x-[177%]',
              )}
            >
              &
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'ml-[50%] inline-flex self-start',
              )}
            >
              Ada
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'ml-[30%] inline-flex self-start',
              )}
            >
              print
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'ml-[10%] inline-flex self-start',
              )}
            >
              Series
            </span>
          </p>
        </div>

        <div
          className={classNames(
            styles.sectionWrapper_container,
            'mt-24 flex flex-col items-center md:mt-48',
          )}
        >
          <p
            className={classNames(
              styles.sectionContent_text,
              'w-full max-w-md text-center',
            )}
          >
            Katz creates an eight-print suite of silkscreens –{' '}
            <i>Alex and Ada</i>, the 1960s to the 1980s – a vivid, two-decade
            portrait of his marriage to Ada, his wife and most enduring muse.
            The editions distil his billboard-flat colour and cinematic crops
            into print, with works like <i>White Hat</i> and{' '}
            <i>Ada with Sunglasses</i> now among his most recognisable images.
          </p>
        </div>

        <div
          ref={imagesRef}
          className="relative flex h-[800lvh] w-0 translate-x-[50%]"
        >
          <motion.div
            className="sticky top-0 flex h-lvh shrink-0 items-center gap-6"
            style={imagesStyle}
          >
            <div
              className={classNames(
                styles.sectionContent_image,
                'aspect-[2/1] h-[60%] ring ring-8 ring-white md:h-[80%]',
              )}
            >
              <Image
                alt="Alex Katz, “Orange Hat”, 1990"
                priority={props.isActive}
                src="https://images.avantarte.org/media/33ViYJSXfNKrrbuprIvlcr3UN88"
                width={720}
              />
            </div>

            <div
              className={classNames(
                styles.sectionContent_image,
                'aspect-[2048/1850] h-[60%] ring ring-8 ring-white md:h-[80%]',
              )}
            >
              <Image
                alt="Alex Katz, “Alex Katz, Self-Portrait (Passing)”, 1990"
                priority={props.isActive}
                src="https://images.avantarte.org/media/33EX5hYA7niQwEzGY7fhhOS4B9e"
                width={720}
              />
            </div>

            <div
              className={classNames(
                styles.sectionContent_image,
                'aspect-[1964/1500] h-[60%] ring ring-8 ring-white md:h-[80%]',
              )}
            >
              <Image
                alt="Alex Katz, “Grey Ribbon”, 1990"
                priority={props.isActive}
                src="https://images.avantarte.org/media/33Vj34LVbgHBmBCyRCyuXLkH7vP"
                width={720}
              />
            </div>

            <div
              className={classNames(
                styles.sectionContent_image,
                'aspect-[2/3] h-[60%] ring ring-8 ring-white md:h-[80%]',
              )}
            >
              <Image
                alt="Alex Katz, “Green Jacket”, 1990"
                priority={props.isActive}
                src="https://images.avantarte.org/media/33ViYME9yLPS73aHpc2vWjvTqYK"
                width={720}
              />
            </div>

            <div
              className={classNames(
                styles.sectionContent_image,
                'aspect-[2048/2560] h-[60%] ring ring-8 ring-white md:h-[80%]',
              )}
            >
              <Image
                alt="Alex Katz, “Sweatshirt 2”, 1990"
                priority={props.isActive}
                src="https://images.avantarte.org/media/33EXEWuKunKHmulksurgaAZbec2"
                width={720}
              />
            </div>

            <div
              className={classNames(
                styles.sectionContent_image,
                'aspect-[2048/3000] h-[60%] ring ring-8 ring-white md:h-[80%]',
              )}
            >
              <Image
                alt="Alex Katz, “Ada with Sunglasses”, 1990"
                priority={props.isActive}
                src="https://images.avantarte.org/media/33EX90RovRDfja9RUjT0kOQ0faf"
                width={720}
              />
            </div>

            <div
              className={classNames(
                styles.sectionContent_image,
                'aspect-[2048/1440] h-[60%] ring ring-8 ring-white md:h-[80%]',
              )}
            >
              <Image
                alt="Alex Katz, “Ada in Hat”, 1990"
                priority={props.isActive}
                src="https://images.avantarte.org/media/33EXChDvAZ0JwXNvR4y0cRivIF9"
                width={720}
              />
            </div>

            <div
              className={classNames(
                styles.sectionContent_image,
                'aspect-[2048/2800] h-[60%] ring ring-8 ring-white md:h-[80%]',
              )}
            >
              <Image
                alt="Alex Katz, “Ada in White Hat”, 1990"
                priority={props.isActive}
                src="https://images.avantarte.org/media/33EXAihfLoLBlZBcLhvESl0rf4L"
                width={720}
              />
            </div>
          </motion.div>
        </div>
      </section>
    );
  },
);

// ----- 2000 ----- //

const Section2000 = forwardRef<HTMLElement>((_props, ref) => {
  return (
    <section
      ref={ref}
      className={classNames(
        styles.sectionWrapper,
        'items-center gap-24 md:gap-48',
      )}
      id="2000"
    >
      <div
        className={classNames(
          styles.sectionWrapper_container,
          'flex flex-col items-center gap-6 md:gap-12',
        )}
      >
        <AnimatedDate
          className={classNames(styles.sectionContent_date)}
          date={2004}
        />

        <p className="flex w-full flex-col items-center text-center">
          <span
            className={classNames(
              styles.sectionContent_title,
              'inline-flex md:-translate-x-[6.5%]',
            )}
          >
            Establishes
          </span>
          <span
            className={classNames(
              styles.sectionContent_title,
              'inline-flex h-0 max-md:self-start md:-translate-x-[200%]',
            )}
          >
            the
          </span>
          <span
            className={classNames(
              styles.sectionContent_title,
              'inline-flex h-0 -translate-x-[4.25%] md:translate-x-[2.25%]',
            )}
          >
            Alex
          </span>
          <span
            className={classNames(
              styles.sectionContent_title,
              'inline-flex max-md:self-end md:translate-x-[150%]',
            )}
          >
            Katz
          </span>
          <span
            className={classNames(
              styles.sectionContent_title,
              'inline-flex md:translate-x-[7.5%]',
            )}
          >
            Foundation
          </span>
        </p>
      </div>

      <div
        className={classNames(
          styles.sectionWrapper_container,
          'grid grid-cols-2 gap-6 md:gap-12',
        )}
      >
        <p
          className={classNames(
            styles.sectionContent_text,
            'w-full max-w-md text-left max-md:col-span-full max-md:text-center md:col-start-2',
          )}
        >
          Katz sets up this initiative to champion artistic excellence, support
          artists and enhance access to contemporary art. The foundation
          pioneers a unique philanthropic model, purchasing works from
          mid-career and emerging artists and donating them directly to
          institutions. To date, the Alex Katz Foundation has donated more than
          700 works and millions of dollars to various arts organisations.
        </p>
      </div>
    </section>
  );
});

// ----- 2010 ----- //

const Section2010 = forwardRef<HTMLElement, { isActive: boolean }>(
  (props, ref) => {
    return (
      <section
        ref={ref}
        className={classNames(
          styles.sectionWrapper,
          'items-center gap-24 md:gap-48',
        )}
        id="2010"
      >
        <div
          className={classNames(
            styles.sectionWrapper_container,
            'flex flex-col items-center gap-6 md:gap-12',
          )}
        >
          <AnimatedDate
            className={classNames(styles.sectionContent_date)}
            date={2010}
          />

          <p className="flex w-full flex-col items-center text-center max-md:translate-x-[4%]">
            <span
              className={classNames(
                styles.sectionContent_title,
                'inline-flex h-0 -translate-x-[328.5%]',
              )}
            >
              A
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'inline-flex translate-x-[45%]',
              )}
            >
              decade
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'inline-flex translate-x-[22.875%]',
              )}
            >
              of
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'inline-flex translate-x-[17.5%]',
              )}
            >
              immersive,
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'inline-flex -translate-x-[3.5%]',
              )}
            >
              monumental
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'inline-flex -translate-x-[6.375%]',
              )}
            >
              landscapes
            </span>
          </p>
        </div>

        <div
          className={classNames(
            styles.sectionWrapper_container,
            'flex flex-col items-center gap-3',
          )}
        >
          <p
            className={classNames(
              styles.sectionContent_text,
              'w-full max-w-md text-center',
            )}
          >
            By 2010, Katz is actively painting tightly cropped, monumental
            landscapes that explore light and the atmospherics of nature –
            Reflection with Lilies 2 is a notable example.
          </p>

          <p
            className={classNames(
              styles.sectionContent_text,
              'w-full max-w-md text-center',
            )}
          >
            In 2015, the exhibition Alex Katz. This Is Now opens at the
            Guggenheim Museum Bilbao, surveying 25 years of his landscape work
            and featuring 35 pieces that employ a “grammar of abstraction” to
            convey what he calls “the present tense” – an explosive “flash” just
            before an image comes into focus.
          </p>

          <p
            className={classNames(
              styles.sectionContent_text,
              'w-full max-w-md text-center',
            )}
          >
            During this period, he also experiments with iPhone photography,
            producing striking, abstracted compositions that signal a
            significant evolution in his visual approach and engagement with our
            changing digital world.
          </p>
        </div>

        <div
          className={classNames(
            styles.sectionWrapper_container,
            'flex flex-col items-center gap-6 md:gap-12',
          )}
        >
          <div
            className={classNames(
              styles.sectionContent_image,
              'aspect-[2048/1640] w-full max-w-7xl ring ring-8 ring-white',
            )}
          >
            <Image
              alt="Alex Katz, “Four Polars”, 2019"
              priority={props.isActive}
              src="https://images.avantarte.org/media/33EZDZG34d1eQgKu2ykS1Kc3LUm"
              width={480}
            />
          </div>

          <div
            className={classNames(
              styles.sectionContent_image,
              'aspect-[2000/1460] w-full max-w-5xl ring ring-8 ring-white',
            )}
          >
            <Image
              alt="Alex Katz, “Reflection with Lilies 2”, 2010"
              priority={props.isActive}
              src="https://images.avantarte.org/media/33EZQe3a5KQfyhJia8YmzBeCB9Z"
              width={480}
            />
          </div>

          <div
            className={classNames(
              styles.sectionContent_image,
              'aspect-[2/1] w-full max-w-6xl ring ring-8 ring-white',
            )}
          >
            <Image
              alt="Alex Katz, “Homage to Monet”, 2009-2010"
              priority={props.isActive}
              src="https://images.avantarte.org/media/33EYINX4JPlXUsAcwt0eEmyizzQ"
              width={480}
            />
          </div>
        </div>
      </section>
    );
  },
);

// ----- 2020 ----- //

const Section2020 = forwardRef<HTMLElement, { isActive: boolean }>(
  (props, ref) => {
    const isMobile = useMediaQuery('sm');
    const shouldReduceMotion = useReducedMotion();

    const imagesRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({
      target: imagesRef,
      offset: ['start start', 'end end'],
    });

    const image2Y = useTransform(
      scrollYProgress,
      [0, 1],
      shouldReduceMotion || isMobile ? ['0%', '0%'] : ['15%', '-15%'],
    );
    const image3Y = useTransform(
      scrollYProgress,
      [0, 1],
      shouldReduceMotion || isMobile ? ['0%', '0%'] : ['25%', '-25%'],
    );
    const image4Y = useTransform(
      scrollYProgress,
      [0, 1],
      shouldReduceMotion || isMobile ? ['0%', '0%'] : ['50%', '0%'],
    );

    const image2Style = isMobile
      ? { rotate: '-2deg', y: image2Y }
      : { rotate: '-2deg', y: image2Y };
    const image3Style = isMobile
      ? { rotate: '1deg', y: image3Y }
      : { rotate: '1deg', y: image3Y };
    const image4Style = isMobile
      ? { rotate: '2deg', y: image4Y }
      : { rotate: '2deg', y: image4Y };

    return (
      <section
        ref={ref}
        className={classNames(
          styles.sectionWrapper,
          'items-center gap-24 md:gap-48',
        )}
        id="2020"
      >
        <div
          className={classNames(
            styles.sectionWrapper_container,
            'flex flex-col items-center',
          )}
        >
          <AnimatedDate
            className={classNames(
              styles.sectionContent_date,
              'h-0 translate-x-[102%]',
            )}
            date={2022}
          />

          <p className="flex w-full flex-col items-center text-center">
            <span
              className={classNames(
                styles.sectionContent_title,
                'inline-flex -translate-x-[68%]',
              )}
            >
              Katz’s
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'inline-flex -translate-x-[54%]',
              )}
            >
              major
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'inline-flex translate-x-[13%]',
              )}
            >
              retrospective
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'inline-flex translate-x-[73%]',
              )}
            >
              opens
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'inline-flex -translate-x-[133%]',
              )}
            >
              at
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'inline-flex -translate-x-[41.75%]',
              )}
            >
              the
            </span>
            <span
              className={classNames(
                styles.sectionContent_title,
                'inline-flex -translate-x-[12%]',
              )}
            >
              Guggenheim
            </span>
          </p>
        </div>

        <div
          className={classNames(
            styles.sectionWrapper_container,
            'flex flex-col items-center',
          )}
        >
          <p
            className={classNames(
              styles.sectionContent_text,
              'w-full max-w-md text-center',
            )}
          >
            In October 2022, Alex Katz’s major retrospective at the Guggenheim
            opens, featuring 75 large canvases and 79 smaller works. Curated by
            Katherine Brinson,{' '}
            <Link
              href="https://www.guggenheim.org/alex-katz?utm_source=AvantArte&utm_medium=referral&utm_campaign=avantarteeditorialpage&utm_content=alexkatzavant2025&utm_term="
              target="_blank"
            >
              <i>Alex Katz: Gathering</i>
            </Link>{' '}
            cements the artist’s enduring influence across nearly a century of
            painting.
          </p>
        </div>

        <div ref={imagesRef} className="relative w-full px-6">
          <div
            className={classNames(
              styles.sectionContent_image,
              'pointer-events-none !sticky top-0 flex h-lvh w-full items-center justify-center',
            )}
          >
            <Video
              alt="Film created on the occasion of “Alex Katz: Gathering,” Solomon R. Guggenheim Museum, 2022-2023"
              className="pointer-events-auto aspect-[1/1] w-full max-w-xl ring ring-8 ring-white md:aspect-[16/9] md:max-w-[80%]"
              cover="https://images.avantarte.org/media/33EcdecoEyaciSvJiKumDJkJJSF"
              src="https://www.youtube.com/embed/D9sqspCthm8?si=4d6mu6U3leLqSMBT&autoplay=1&controls=0"
            />
          </div>

          <div className="pointer-events-none sticky top-0 flex h-lvh w-full items-center justify-center gap-6 max-md:flex-col md:gap-12">
            <motion.div
              className={classNames(
                styles.sectionContent_image,
                'pointer-events-auto aspect-[1/1] w-full max-w-72 ring ring-8 ring-white md:max-w-3xl',
              )}
              style={image2Style}
            >
              <Image
                alt="A view of the “Alex Katz: Gathering,” exhibition at the Solomon R. Guggenheim Museum, 2022-2023"
                priority={props.isActive}
                src="https://images.avantarte.org/media/33EcWvLBA9ARj5bubdh7i979rbw"
                width={480}
              />
            </motion.div>

            <motion.div
              className={classNames(
                styles.sectionContent_image,
                'pointer-events-auto aspect-[3/4] w-full max-w-60 ring ring-8 ring-white md:max-w-2xl',
              )}
              style={image3Style}
            >
              <Image
                alt="A view of the “Alex Katz: Gathering,” exhibition at the Solomon R. Guggenheim Museum, 2022-2023"
                priority={props.isActive}
                src="https://images.avantarte.org/media/33EcZLKoFUZUdEv0Z4ovH9AGbwh"
                width={480}
              />
            </motion.div>
          </div>

          <motion.div
            className={classNames(
              'pointer-events-none sticky top-0 flex h-lvh w-full items-center justify-center',
            )}
            style={image4Style}
          >
            <div
              className={classNames(
                styles.sectionContent_image,
                'pointer-events-auto aspect-[4/3] w-full max-w-7xl ring ring-8 ring-white',
              )}
            >
              <Image
                alt="A view of the “Alex Katz: Gathering,” exhibition at the Solomon R. Guggenheim Museum, 2022-2023"
                priority={props.isActive}
                src="https://images.avantarte.org/media/33EcbhDwomCqOcxyixPyYO4sIV6"
                width={720}
              />
            </div>
          </motion.div>
        </div>
      </section>
    );
  },
);

// ----- SectionQuote ----- //

const SectionQuote = forwardRef<
  HTMLElement,
  {
    isActive: boolean;
    images: { id: string; src: string }[];
  }
>((props, ref) => {
  const isMobile = useMediaQuery('sm');

  const mid = Math.floor(props.images.length / 2);

  const displayImages = isMobile
    ? props.images.slice(mid - 1, mid + 2)
    : props.images;

  const imagesRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: imagesRef,
    offset: ['start start', 'end end'],
  });

  const imagesWidth = useTransform(
    scrollYProgress,
    [0, 1],
    [
      `${100 / displayImages.length}%`,
      `${100 / (displayImages.length - 1.5)}%`,
    ],
  );
  const imagesGap = useTransform(scrollYProgress, [0, 1], ['0%', '-5%']);

  const zIndexMap = createZIndexMap(displayImages.length);

  return (
    <section ref={ref} className={styles.sectionQuote_wrapper}>
      <div ref={imagesRef} className={styles.sectionQuote_scrollContainer}>
        <div className={styles.sectionQuote_images}>
          {displayImages.map(({ id, src }, index) => {
            return (
              <motion.div
                key={id}
                className={classNames(
                  styles.sectionContent_image,
                  'h-full shrink-0',
                )}
                style={{
                  zIndex: zIndexMap[index],
                  width: imagesWidth,
                  marginLeft: imagesGap,
                }}
              >
                <Image
                  alt={src}
                  priority={props.isActive}
                  src={src}
                  width={720}
                />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
});

// ----- Insights ----- //

const SectionInsights = forwardRef<HTMLElement, { isActive: boolean }>(
  (props, ref) => {
    const drawEndDate = new Date('2025-10-30T16:00:00Z'); // start date in UTC
    const isDrawClosed = isPast(drawEndDate);

    return (
      <section
        ref={ref}
        className={classNames(
          styles.sectionWrapper,
          '!min-h-px items-center !py-0',
        )}
        id="insights"
      >
        <div
          className={classNames(
            styles.sectionWrapper_container,
            'flex flex-col items-center',
          )}
        >
          <Link
            className={classNames(
              styles.sectionContent_image,
              'group relative grid aspect-[1/1] w-full max-w-4xl grid-cols-1 grid-rows-1 md:aspect-[3/2]',
            )}
            href={
              isDrawClosed
                ? 'https://avantarte.com/insights/articles/alex-katz-avant-arte-for-the-guggenheim'
                : 'https://avantarte.com/products/alex-katz-ada-ada'
            }
          >
            <Image
              alt={
                isDrawClosed
                  ? 'A view of the “Alex Katz: Gathering,” exhibition at the Solomon R. Guggenheim Museum, 2022-2023'
                  : 'Alex Katz, “Ada Ada”, a new limited edition print launched in collaboration with Avant Arte for the Guggenheim'
              }
              priority={props.isActive}
              src={
                isDrawClosed
                  ? 'https://images.avantarte.org/media/33EcWvLBA9ARj5bubdh7i979rbw'
                  : 'https://images.avantarte.org/media/33VnHdCPsig4pgSyhouql6ZEf6j'
              }
              width={720}
            />

            <span className="z-20 flex h-full w-full items-start justify-end gap-3 bg-black/30 p-6 group-hover:bg-black/20 motion-safe:transition max-md:flex-col md:items-end md:p-8">
              <span className="text-balance text-md text-white sm:text-lg md:text-xl">
                {isDrawClosed ? 'Article' : 'Print'}{' '}
                <strong>Alex Katz & Avant Arte for the Guggenheim</strong>
              </span>
              <span className="flex shrink-0 items-center justify-center gap-2 rounded-full bg-black/30 px-6 py-3 text-white backdrop-blur-2xl">
                {isDrawClosed ? 'Read more' : 'View edition'}

                <svg
                  aria-hidden
                  className="size-5 group-hover:-translate-y-[20%] group-hover:translate-x-[20%] motion-safe:transition"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7 7H17M17 7V17M17 7L7 17"
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </span>
          </Link>
        </div>
      </section>
    );
  },
);

// ----- Footer ----- //

const SectionCredits = forwardRef<HTMLElement, { isActive: boolean }>(
  (props, ref) => {
    return (
      <section
        ref={ref}
        className={classNames(styles.sectionWrapper, 'items-center')}
        id="credits"
      >
        <div
          className={classNames(
            styles.sectionWrapper_container,
            'flex flex-col items-center gap-12',
          )}
        >
          <div className="grid w-full max-w-2xl grid-cols-2 gap-6">
            <div
              className={classNames(
                styles.sectionContent_image,
                'aspect-[3/4]',
              )}
            >
              <Image
                alt="Black and white photograph of Alex Katz, 1950"
                priority={props.isActive}
                src="https://images.avantarte.org/media/33Ef48vzpfYRXmeI5eU0v0malNt"
                width={240}
              />
            </div>
            <div
              className={classNames(
                styles.sectionContent_image,
                'aspect-[3/4]',
              )}
            >
              <Image
                alt="Black and white photograph of Alex Katz, smiling, wearing a dark suit and white dress shirt, and standing in front of a painting, 2010"
                priority={props.isActive}
                src="https://images.avantarte.org/media/33Ef477Pk8Soa46bzoFXuCVLaLT"
                width={240}
              />
            </div>
          </div>

          <div className="flex w-full max-w-2xl flex-col divide-y divide-black/10">
            <div className="flex w-full gap-x-6 gap-y-2 pb-6 max-md:flex-col">
              <p
                className={classNames(
                  styles.sectionContent_text,
                  'shrink-0 opacity-60 md:w-48',
                )}
              >
                Credits
              </p>
              <div className="flex w-full flex-col gap-3">
                <p className={classNames(styles.sectionContent_text, 'w-full')}>
                  All artwork images ©2025 Alex Katz, Artists Rights Society
                  (ARS), NY, unless otherwise noted. Archive images of Alex Katz
                  are sourced from Alex Katz (Electra Rizzoli NY, 2020), © 2020
                  Alex Katz.
                </p>
                <p className={classNames(styles.sectionContent_text, 'w-full')}>
                  The 1946-1949 section includes subway drawings courtesy of
                  Alex Katz, VAGA, New York, NY / DACS, London, courtesy of
                  Timothy Taylor, sourced from Vogue. Archive image courtesy of
                  the Whitney Museum of American Art, New York; gift of Alex
                  Katz, © Rudy Burckhardt / Artists Rights Society (ARS), New
                  York. The painting Art School by Alex Katz is © Alex Katz,
                  VAGA at Artists Rights Society (ARS), NY, gift of the artist.
                  Additional archive images are credited to Fred W. McDarrah,
                  Ada and Alex Katz, April 6, 1961, Smithsonian Archives of
                  American Art. The 1977 section features a video excerpt from
                  Alex Katz: What About Style?, credit Heinz Peter Schwerfel,
                  with archive images credited to Chuck DeLaney. The 1986
                  section includes an article by Emily Tobias, Vassar Newspaper
                  & Magazine Archive, Miscellany News, Volume LXXVI, Number 8,
                  April 4, 1986.
                </p>
                <p className={classNames(styles.sectionContent_text, 'w-full')}>
                  Final film credit to The Guggenheim, New York. The video was
                  created on the occasion of Alex Katz: Gathering, Solomon R.
                  Guggenheim Museum, October 21, 2022–February 20, 2023.
                  Installation images © Solomon R. Guggenheim Foundation, New
                  York, photographed by Ariel Ione Williams and Midge Wattles.
                </p>
                <p className={classNames(styles.sectionContent_text, 'w-full')}>
                  Archive portraits are courtesy of the artist’s studio: Alex
                  Katz in New York City, 1956; Portrait of Alex Katz, 2011,
                  photographed by Vivien Bittencourt; and Alex in Maine 1, 2021,
                  photographed by Isaac Katz.
                </p>
              </div>
            </div>
            <div className="flex w-full gap-x-6 gap-y-2 pt-6 max-md:flex-col">
              <p
                className={classNames(
                  styles.sectionContent_text,
                  'shrink-0 opacity-60 md:w-48',
                )}
              >
                Follow us
              </p>
              <div className="flex w-full flex-col gap-3">
                <p className={classNames(styles.sectionContent_text, 'w-full')}>
                  Avant Arte exists to make discovering and owning art radically
                  more accessible. We collaborate with artists worldwide to put
                  their work in new places and introduce them to the next
                  generation of collectors.
                </p>

                <ul
                  className={classNames(styles.sectionContent_text, 'w-full')}
                >
                  <li>
                    <Link href="https://avantarte.com">avantarte.com</Link>
                  </li>
                  <li>
                    <Link href="https://www.instagram.com/avant.arte">
                      instagram.com/avant.arte
                    </Link>
                  </li>
                  <li>
                    <Link href="https://x.com/avant_arte">
                      x.com/avant_arte
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex max-w-2xl items-center justify-center gap-6 md:gap-12">
            <Link href="https://avantarte.com">
              <span className="sr-only">Avant Arte</span>
              <svg
                className="h-8 md:h-12"
                viewBox="0 0 202 48"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.1861 32.4016H16.5425C16.9262 31.2256 17.2857 30.2176 17.8851 28.4656H24.622C25.2214 30.2176 25.5569 31.2256 25.9406 32.4016H29.393C28.8415 31.0576 27.8346 28.4656 27.1632 26.7135L25.5091 22.4656C24.526 19.9456 23.3513 16.9216 22.8959 15.6016H19.6592C19.2037 16.9216 18.0529 19.9456 17.07 22.4656L15.4157 26.7135C14.7445 28.4656 13.7375 31.0576 13.1861 32.4016ZM18.7723 25.8256L19.7792 22.8975C20.3784 21.1695 20.9778 19.3936 21.1936 18.6016H21.2895C21.4813 19.3936 22.1287 21.1695 22.7279 22.8975L23.7349 25.8256H18.7723ZM37.2646 32.4016H40.5251C40.9568 31.0815 42.0596 28.2496 43.0665 25.5136L44.6489 21.2655C45.44 19.1056 46.303 16.9456 46.8545 15.6016H43.4261C43.0426 16.8495 42.4911 18.4336 41.9877 19.9696L40.3334 25.0815C39.734 26.8815 39.2067 28.5376 38.9908 29.3295H38.9189C38.7031 28.5376 38.1756 26.8815 37.5763 25.0815L35.9221 19.9696L34.4836 15.6016H30.9353C31.4868 16.9456 32.3499 19.1056 33.141 21.2655L34.7234 25.5136C35.7303 28.2496 36.8332 31.0815 37.2646 32.4016ZM48.397 32.4016H51.7534C52.1369 31.2256 52.4966 30.2176 53.096 28.4656H59.8328C60.4321 30.2176 60.7678 31.2256 61.1514 32.4016H64.6037C64.0524 31.0576 63.0453 28.4656 62.3741 26.7135L60.7198 22.4656C59.7369 19.9456 58.5622 16.9216 58.1066 15.6016H54.8701C54.4146 16.9216 53.2638 19.9456 52.2808 22.4656L50.6265 26.7135C49.9553 28.4656 48.9484 31.0576 48.397 32.4016ZM53.983 25.8256L54.9899 22.8975C55.5893 21.1695 56.1887 19.3936 56.4044 18.6016H56.5004C56.6921 19.3936 57.3394 21.1695 57.9388 22.8975L58.9457 25.8256H53.983ZM70.2347 32.4016H73.3994C73.3514 31.2016 73.2796 29.2576 73.2796 27.0256V23.7855C73.2796 21.9856 73.2556 21.0495 73.2556 20.5935H73.3994L81.7425 32.4016H84.4997C84.4517 31.2016 84.3797 29.2576 84.3797 27.0256V21.1695C84.3797 18.9135 84.4517 16.8015 84.4997 15.6016H81.359C81.407 16.8015 81.4788 18.9135 81.4788 21.1695V24.4816C81.4788 26.1616 81.4788 26.7376 81.5028 27.1936H81.359L72.9919 15.6016H70.2347C70.2827 16.8015 70.3546 18.6975 70.3546 21.1695V27.0256C70.3546 29.2576 70.2827 31.2016 70.2347 32.4016ZM90.5179 18.2896L95.9841 18.2415C95.9841 19.1056 96.0079 20.0415 96.0079 20.9776V27.0256C96.0079 29.1135 95.9361 31.1536 95.8881 32.4016H99.1486C99.1008 31.1536 99.0288 29.1135 99.0288 27.0256V20.9776C99.0288 20.0415 99.0528 19.1056 99.0528 18.2415L104.543 18.2896V15.6016H90.5179V18.2896ZM116.878 32.4016H120.234C120.618 31.2256 120.977 30.2176 121.577 28.4656H128.313C128.913 30.2176 129.248 31.2256 129.632 32.4016H133.084C132.533 31.0576 131.526 28.4656 130.855 26.7135L129.2 22.4656C128.218 19.9456 127.043 16.9216 126.587 15.6016H123.351C122.895 16.9216 121.744 19.9456 120.761 22.4656L119.107 26.7135C118.436 28.4656 117.429 31.0576 116.878 32.4016ZM122.464 25.8256L123.471 22.8975C124.07 21.1695 124.669 19.3936 124.885 18.6016H124.981C125.173 19.3936 125.82 21.1695 126.419 22.8975L127.426 25.8256H122.464ZM148.497 32.4016H152.405V32.2575C150.727 30.1936 148.881 27.4096 147.729 25.6576C150.487 25.0096 151.637 22.7295 151.637 20.7135C151.637 17.4496 149.408 15.6016 146.099 15.6016H138.715C138.763 16.8495 138.835 18.8896 138.835 20.9776V27.0256C138.835 29.1135 138.763 31.1536 138.715 32.4016H141.977C141.927 31.1536 141.856 29.1135 141.856 27.0256V25.8256H144.469C146.075 28.4415 147.035 30.0015 148.497 32.4016ZM141.856 23.1855V20.9776C141.856 20.0415 141.88 19.1056 141.88 18.2415H145.62C147.418 18.2415 148.544 19.0815 148.544 20.7135C148.544 22.3455 147.418 23.1855 145.62 23.1855H141.856ZM156.939 18.2896L162.404 18.2415C162.404 19.1056 162.429 20.0415 162.429 20.9776V27.0256C162.429 29.1135 162.356 31.1536 162.308 32.4016H165.568C165.521 31.1536 165.449 29.1135 165.449 27.0256V20.9776C165.449 20.0415 165.473 19.1056 165.473 18.2415L170.964 18.2896V15.6016H156.939V18.2896ZM176.946 32.4016H188.814V29.7136L180.111 29.7855C180.111 28.9216 180.087 27.9855 180.087 27.0256V25.2495H187.854V22.6095H180.087V20.9776C180.087 20.0176 180.111 19.0815 180.111 18.2176L188.695 18.2896V15.6016H176.946C176.994 16.8495 177.067 18.8896 177.067 20.9776V27.0256C177.067 29.1135 176.994 31.1536 176.946 32.4016Z"
                  fill="currentColor"
                />
              </svg>
            </Link>
            <Link href="https://www.guggenheim.org/">
              <span className="sr-only">Guggenheim New York</span>
              <svg
                className="h-8 md:h-12"
                viewBox="0 0 134 48"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0 14.5248C0 10.8264 2.78988 8 6.432 8C8.89224 8 11.2158 9.46351 12.0707 11.5535H9.2058C8.68186 10.8935 7.5911 10.3515 6.44138 10.3515C4.23574 10.3515 2.70546 12.0189 2.70546 14.5234C2.70546 17.0279 4.24378 18.6524 6.48426 18.6524C8.52106 18.6524 9.92404 17.4598 10.1277 15.5214H5.6481V13.3631H12.5625V14.7595C12.5625 18.6188 10.1023 21.0133 6.36634 21.0133C2.63042 21.0133 0 18.3063 0 14.5248ZM29.0847 14.5248C29.0847 10.8264 31.8746 8 35.5167 8C37.9769 8 40.3005 9.46351 41.1554 11.5535H38.2905C37.7666 10.8935 36.6758 10.3515 35.5261 10.3515C33.3204 10.3515 31.7902 12.0189 31.7902 14.5234C31.7902 17.0279 33.3285 18.6524 35.569 18.6524C37.6058 18.6524 39.0087 17.4598 39.2124 15.5214H34.7328V13.3631H41.6472V14.7595C41.6472 18.6188 39.187 21.0133 35.451 21.0133C31.7151 21.0133 29.086 18.3049 29.086 14.5234L29.0847 14.5248ZM44.4424 14.5248C44.4424 10.8264 47.2323 8 50.8744 8C53.3347 8 55.6582 9.46351 56.5132 11.5535H53.6482C53.1243 10.8935 52.0335 10.3515 50.8838 10.3515C48.6782 10.3515 47.1479 12.0189 47.1479 14.5234C47.1479 17.0279 48.6862 18.6524 50.9267 18.6524C52.9635 18.6524 54.3665 17.4598 54.5702 15.5214H50.0905V13.3631H57.0049V14.7595C57.0049 18.6188 54.5447 21.0133 50.8088 21.0133C47.0729 21.0133 44.4438 18.3049 44.4438 14.5234L44.4424 14.5248ZM60.5372 8.20256H69.0328V10.4025H63.1998V13.3644H68.5839V15.5228H63.1998V18.6108H69.0408V20.8107H60.5372V8.20256ZM72.7942 8.20256H75.1271L80.5541 16.1398H80.672V8.20256H83.3346V20.8107H81.0271L75.5747 12.856H75.4567V20.8107H72.7942V8.20256ZM101.56 8.20256H110.056V10.4025H104.223V13.3644H109.607V15.5228H104.223V18.6108H110.064V20.8107H101.56V8.20256ZM113.817 8.20256H116.48V20.8107H113.817V8.20256ZM120.222 8.20256H123.569L127.035 16.9098H127.162L130.635 8.20256H134V20.8107H131.396V12.5864H131.269L127.998 20.8107H126.189L122.934 12.5864H122.807V20.8107H120.221V8.20256H120.222ZM15.7115 16.2753V8.20256H18.366V16.0553C18.366 17.6637 19.3375 18.6698 20.9268 18.6698C22.516 18.6698 23.4875 17.6624 23.4875 16.0553V8.20256H26.1421V16.2753C26.1421 19.1688 24.1213 21.0146 20.9268 21.0146C17.7322 21.0146 15.7115 19.1702 15.7115 16.2753ZM87.0638 8.20256H89.7264V13.406H95.1279V8.20256H97.7825V20.8107H95.1279V15.5979H89.7264V20.8107H87.0638V8.20256ZM1.53564 27.1879H2.99892L9.86374 37.1387H9.98166V27.1879H11.4959V39.7961H9.99908L3.16776 29.8949H3.04984V39.7961H1.53564V27.1879ZM15.9447 27.1879H23.5157V28.5414H17.4589V32.7549H23.1123V34.1084H17.4589V38.4412H23.6001V39.7948H15.9447V27.1866V27.1879ZM26.4757 27.1879H28.0073L30.6324 37.4607H30.7584L33.433 27.1879H35.1402L37.8242 37.4607H37.9421L40.5672 27.1879H42.0894L38.6818 39.7961H37.0912L34.3482 29.2444H34.2303L31.4793 39.7961H29.89L26.4744 27.1879H26.4757ZM51.0942 34.6007L46.569 27.1879H48.3016L51.8017 33.0943H51.9196L55.4023 27.1879H57.1523L52.6097 34.6007V39.7961H51.0955V34.6007H51.0942ZM74.6621 27.1879H78.8349C81.4854 27.1879 83.1926 28.7118 83.1926 31.0298C83.1926 32.7133 82.2921 33.9743 80.7953 34.5417L83.6468 39.7961H81.8807L79.2476 34.8623C79.1136 34.8703 78.9863 34.8703 78.8523 34.8703H76.1777V39.7948H74.6635V27.1866L74.6621 27.1879ZM78.8014 33.5007C80.593 33.5007 81.6623 32.5523 81.6623 31.0298C81.6623 29.5073 80.5849 28.5414 78.7853 28.5414H76.1777V33.5007H78.8027H78.8014ZM87.7995 27.1879H89.3137V32.5778H89.4316L95.0435 27.1879H97.079L90.8359 33.1278L97.1714 39.7961H95.1775L89.4316 33.6872L89.3137 33.6952V39.7961H87.7995V27.1879ZM58.4307 33.5007C58.4307 29.7608 61.1536 26.9854 64.8158 26.9854C68.478 26.9854 71.1929 29.7608 71.1929 33.5007C71.1929 37.2407 68.4861 40 64.8158 40C61.1455 40 58.4307 37.2326 58.4307 33.5007ZM64.8158 38.5781C67.5816 38.5781 69.6358 36.4197 69.6358 33.5007C69.6358 30.5818 67.5802 28.4073 64.8158 28.4073C62.0514 28.4073 59.9878 30.5737 59.9878 33.5007C59.9878 36.4278 62.0433 38.5781 64.8158 38.5781Z"
                  fill="currentColor"
                />
              </svg>
            </Link>
          </div>
        </div>
      </section>
    );
  },
);

// ----- ----- //

type PageProps = {
  locale: string;
  locales: string[];
  ogImageUrl: string;
  preview: boolean;
  story: Franchises;
  backgroundImage: StoryblokImage;
};

export async function getStaticProps(
  context: GetStaticPropsContext,
): Promise<GetStaticPropsResult<PageProps>> {
  const translations = await pageTranslations(context.locale || 'en', []);

  const page: StoryResponse<Franchises> = await getStoryblokPage(
    'cdn/stories/insights/articles/10-decades-of-alex-katz',
    {
      locale: context.locale,
      preview: context.preview,
    },
  );

  if (!page) {
    return {
      notFound: true,
    };
  }

  const story = page.data?.story;
  const heroPicker = story.content.heroPicker[0] as HeroImage;
  const backgroundImage = heroPicker.image;

  const ogImageUrl = await imageOgStaticLoader({
    titleKey: story.content.title,
    imageSrc: backgroundImage.filename,
    imageWidth: 1200,
    imageHeight: 630,
    overlay: true,
  });

  return {
    props: {
      locale: context.locale || 'en',
      locales: context.locales || [],
      preview: Boolean(context.preview),
      ogImageUrl,
      story,
      backgroundImage,
      ...translations,
    },
  };
}

const Page = ({
  locale,
  ogImageUrl,
  preview,
  story,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  useTheme('light');

  const page = useStoryblok(story, preview, locale);

  const { asPath } = useRouter();
  const fallbackCanonicalUrl = toCanonicalUrl({
    path: asPath,
    locale,
    hostname: process.env.NEXT_PUBLIC_HOSTNAME!,
  });

  useEffect(() => {
    document.documentElement.classList.add('no-scrollbar');

    return () => {
      document.documentElement.classList.remove('no-scrollbar');
    };
  }, []);

  const [activeSection, setActiveSection] = useState<string | null>(null);

  // Section refs
  const sectionHeroRef = useRef<HTMLElement>(null);
  const section1920Ref = useRef<HTMLElement>(null);
  const section1940Ref = useRef<HTMLElement>(null);
  const section1950Ref = useRef<HTMLElement>(null);
  const section1960Ref = useRef<HTMLElement>(null);
  const section1970Ref = useRef<HTMLElement>(null);
  const section1980Ref = useRef<HTMLElement>(null);
  const section1990Ref = useRef<HTMLElement>(null);
  const section2000Ref = useRef<HTMLElement>(null);
  const section2010Ref = useRef<HTMLElement>(null);
  const section2020Ref = useRef<HTMLElement>(null);
  const sectionInsightsRef = useRef<HTMLElement>(null);
  const sectionCreditsRef = useRef<HTMLElement>(null);

  // Section configurations
  const sections = useMemo(
    () => [
      {
        ref: sectionHeroRef,
        name: 'hero',
        vars: {
          '--background': '#FFFFFF',
          '--headline': '#954E00',
          '--text': '#954E00',
          '--date': '#FBB402',
        },
      },
      {
        ref: section1920Ref,
        name: '1920',
        vars: {
          '--background': '#FFFFFF',
          '--headline': '#954E00',
          '--text': '#954E00',
          '--date': '#FBB402',
        },
      },
      {
        ref: section1940Ref,
        name: '1940',
        vars: {
          '--background': '#FBB402',
          '--headline': '#FFFFFF',
          '--text': '#621B00',
          '--date': '#621B00',
        },
      },
      {
        ref: section1950Ref,
        name: '1950',
        vars: {
          '--background': '#2769C6',
          '--headline': '#FFFFFF',
          '--text': '#FFFFFF',
          '--date': '#000360',
        },
      },
      {
        ref: section1960Ref,
        name: '1960',
        vars: {
          '--background': '#FFFFFF',
          '--headline': '#005700',
          '--text': '#005700',
          '--date': '#36BD36',
        },
      },
      {
        ref: section1970Ref,
        name: '1970',
        vars: {
          '--background': '#000027',
          '--headline': '#FFFFFF',
          '--text': '#A28AF3',
          '--date': '#A28AF3',
        },
      },
      {
        ref: section1980Ref,
        name: '1980',
        vars: {
          '--background': '#C80016',
          '--headline': '#BDD2EC',
          '--text': '#FFFFFF',
          '--date': '#FFFFFF',
        },
      },
      {
        ref: section1990Ref,
        name: '1990',
        vars: {
          '--background': '#F6DEC6',
          '--headline': '#5D452D',
          '--text': '#5D452D',
          '--date': '#907860',
        },
      },
      {
        ref: section2000Ref,
        name: '2000',
        vars: {
          '--background': '#F6A4CB',
          '--headline': '#5D0B32',
          '--text': '#5D0B32',
          '--date': '#FFFFFF',
        },
      },
      {
        ref: section2010Ref,
        name: '2010',
        vars: {
          '--background': '#001700',
          '--headline': '#4D9B2B',
          '--text': '#FFFFFF',
          '--date': '#FFFFFF',
        },
      },
      {
        ref: section2020Ref,
        name: '2020',
        vars: {
          '--background': '#01271E',
          '--headline': '#7FE5DF',
          '--text': '#FFFFFF',
          '--date': '#FFFFFF',
        },
      },
      {
        ref: sectionInsightsRef,
        name: 'insights',
        vars: {
          '--background': '#FFFFFF',
          '--headline': '#000000',
          '--text': '#000000',
          '--date': '#000000',
        },
      },
      {
        ref: sectionCreditsRef,
        name: 'credits',
        vars: {
          '--background': '#FFFFFF',
          '--headline': '#000000',
          '--text': '#000000',
          '--date': '#000000',
        },
      },
    ],
    [
      sectionHeroRef,
      section1920Ref,
      section1940Ref,
      section1950Ref,
      section1960Ref,
      section1970Ref,
      section1980Ref,
      section1990Ref,
      section2000Ref,
      section2010Ref,
      section2020Ref,
      sectionInsightsRef,
      sectionCreditsRef,
    ],
  );

  // Create scroll progress for each section
  const sectionHeroScroll = useScroll({
    target: sectionHeroRef,
    offset: ['start center', 'end center'],
  });

  const section1920Scroll = useScroll({
    target: section1920Ref,
    offset: ['start center', 'end center'],
  });

  const section1940Scroll = useScroll({
    target: section1940Ref,
    offset: ['start center', 'end center'],
  });

  const section1950Scroll = useScroll({
    target: section1950Ref,
    offset: ['start center', 'end center'],
  });

  const section1960Scroll = useScroll({
    target: section1960Ref,
    offset: ['start center', 'end center'],
  });

  const section1970Scroll = useScroll({
    target: section1970Ref,
    offset: ['start center', 'end center'],
  });

  const section1980Scroll = useScroll({
    target: section1980Ref,
    offset: ['start center', 'end center'],
  });

  const section1990Scroll = useScroll({
    target: section1990Ref,
    offset: ['start center', 'end center'],
  });

  const section2000Scroll = useScroll({
    target: section2000Ref,
    offset: ['start center', 'end center'],
  });

  const section2010Scroll = useScroll({
    target: section2010Ref,
    offset: ['start center', 'end center'],
  });

  const section2020Scroll = useScroll({
    target: section2020Ref,
    offset: ['start center', 'end center'],
  });

  const sectionInsightsScroll = useScroll({
    target: sectionInsightsRef,
    offset: ['start center', 'end center'],
  });

  const sectionCreditsScroll = useScroll({
    target: sectionCreditsRef,
    offset: ['start center', 'end center'],
  });

  const sectionScrolls = useMemo(
    () => [
      sectionHeroScroll,
      section1920Scroll,
      section1940Scroll,
      section1950Scroll,
      section1960Scroll,
      section1970Scroll,
      section1980Scroll,
      section1990Scroll,
      section2000Scroll,
      section2010Scroll,
      section2020Scroll,
      sectionInsightsScroll,
      sectionCreditsScroll,
    ],
    [
      sectionHeroScroll,
      section1920Scroll,
      section1940Scroll,
      section1950Scroll,
      section1960Scroll,
      section1970Scroll,
      section1980Scroll,
      section1990Scroll,
      section2000Scroll,
      section2010Scroll,
      section2020Scroll,
      sectionInsightsScroll,
      sectionCreditsScroll,
    ],
  );

  // Track which section is currently in view
  useEffect(() => {
    const unsubscribes = sectionScrolls.map((scroll, index) => {
      return scroll.scrollYProgress.on('change', (progress) => {
        const sectionName = sections[index].name;

        if (progress > 0 && progress < 1) {
          if (activeSection !== sectionName) {
            setActiveSection(sectionName);
            const { vars } = sections[index];
            Object.entries(vars).forEach(([prop, value]) => {
              document.documentElement.style.setProperty(prop, value);
            });
          }
        }
      });
    });

    return () => {
      unsubscribes.forEach((unsub) => unsub());
    };
  }, [activeSection, sectionScrolls, sections]);

  const currentBackgroundColor = useMemo(() => {
    const currentSection = sections.find((s) => s.name === activeSection);
    return currentSection?.vars['--background'] || '#FFFFFF';
  }, [activeSection, sections]);

  return (
    <main className={styles.pageWrapper}>
      <style>
        {
          'html { scroll-behavior: smooth } @media (prefers-reduced-motion: reduce) { html { scroll-behavior: auto } }'
        }
      </style>
      <Head
        description={page.content.seo.description}
        theme="light"
        title={page.content.seo.title}
        urls={{ canonical: fallbackCanonicalUrl }}
      />

      <SocialMetadata
        description={page.content.seo.description}
        imageUrl={ogImageUrl}
        title={page.content.seo.title}
        type="article"
        url={fallbackCanonicalUrl}
      />

      <NextHead>
        <meta content={currentBackgroundColor} name="theme-color" />
        <meta content={currentBackgroundColor} name="msapplication-TileColor" />
      </NextHead>

      <Menu active={activeSection} />

      {/* <div className="pointer-events-none fixed inset-0 z-[9999] mx-auto flex h-full w-full max-w-7xl justify-between">
        <span className="h-full w-px bg-amber-500" />
        <span className="h-full w-px bg-amber-500" />
        <span className="h-full w-px bg-amber-500" />
        <span className="h-full w-px bg-amber-500" />
        <span className="h-full w-px bg-amber-500" />
        <span className="h-full w-px bg-amber-500" />
        <span className="h-full w-px bg-amber-500" />
        <span className="h-full w-px bg-amber-500" />
        <span className="h-full w-px bg-amber-500" />
        <span className="h-full w-px bg-amber-500" />
        <span className="h-full w-px bg-amber-500" />
        <span className="h-full w-px bg-amber-500" />
        <span className="h-full w-px bg-amber-500" />
      </div> */}

      {/* <div className="pointer-events-none fixed inset-0 z-[9999] mx-auto flex h-full w-full flex-col justify-between">
        <span className="h-px w-full bg-red-500" />
        <span className="h-px w-full bg-red-500" />
        <span className="h-px w-full bg-red-500" />
      </div> */}

      <SectionHero ref={sectionHeroRef} />
      <Section1920 ref={section1920Ref} isActive={activeSection === '1920'} />
      <Section1940 ref={section1940Ref} isActive={activeSection === '1940'} />
      <Section1950 ref={section1950Ref} isActive={activeSection === '1950'} />
      <SectionQuote
        images={[
          {
            id: 'alex-katz-black-dress-1',
            src: 'https://images.avantarte.org/media/33CLPM7XKii7ZZSi3GJKJMwpboA',
          },
          {
            id: 'alex-katz-black-dress-2',
            src: 'https://images.avantarte.org/media/33CLPJ8P61vQkNnOM2VU5QAfhUb',
          },
          {
            id: 'alex-katz-black-dress-3',
            src: 'https://images.avantarte.org/media/33CLPGPsssW1uA3NKwf8aXvzKSM',
          },
          {
            id: 'alex-katz-black-dress-4',
            src: 'https://images.avantarte.org/media/33CLPH8VUKi56VNQ3WzarqX70Nq',
          },
          {
            id: 'alex-katz-black-dress-5',
            src: 'https://images.avantarte.org/media/33CLPHphjVWg2uTwDU1nvEbvWU0',
          },
        ]}
        isActive={activeSection === 'Quote'}
      />
      <Section1960 ref={section1960Ref} isActive={activeSection === '1960'} />
      <Section1970 ref={section1970Ref} isActive={activeSection === '1970'} />
      <Section1980 ref={section1980Ref} isActive={activeSection === '1980'} />
      <SectionQuote
        images={[
          {
            id: 'alex-katz-couples-1',
            src: 'https://images.avantarte.org/media/33EV6II2f4RlmwPH5qmwfLLNYeo',
          },
          {
            id: 'alex-katz-couples-2',
            src: 'https://images.avantarte.org/media/33EV6GSQV89rO5nNZZ3jfmCZ4ax',
          },
          {
            id: 'alex-katz-couples-3',
            src: 'https://images.avantarte.org/media/33EV6KySVadr5Qtz9h5ABL36Hzo',
          },
          {
            id: 'alex-katz-couples-4',
            src: 'https://images.avantarte.org/media/33EVGX0wkgtTjGB3Eb0ttlkVSKw',
          },
          {
            id: 'alex-katz-couples-5',
            src: 'https://images.avantarte.org/media/33EV6K6Fo2Ur5OsM8uCZfCJG3v7',
          },
        ]}
        isActive={activeSection === 'Quote'}
      />
      <Section1990 ref={section1990Ref} isActive={activeSection === '1990'} />
      <Section2000 ref={section2000Ref} />
      <Section2010 ref={section2010Ref} isActive={activeSection === '2010'} />
      <Section2020 ref={section2020Ref} isActive={activeSection === '2020'} />
      <SectionInsights
        ref={sectionInsightsRef}
        isActive={activeSection === 'insights'}
      />
      <SectionCredits
        ref={sectionCreditsRef}
        isActive={activeSection === 'credits'}
      />

      <HideHubspot />
    </main>
  );
};

export default Page;
